import React, { useState, useEffect, useRef, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowLeft, TrendingUp, TrendingDown, Minus, Activity, BarChart3, Users, MessageSquare, DollarSign, RefreshCw, AlertCircle, HelpCircle, Maximize2, X, Flame } from 'lucide-react';
import { useParams, useNavigate } from 'react-router-dom';
import { apiService, type TickerContribution, type SpikeSignal } from '../../services/api';
import { useAuth } from '../../contexts/AuthContext';
import { LineChart, Line, AreaChart, Area, PieChart, Pie, Cell, XAxis, YAxis, Tooltip, ResponsiveContainer, ReferenceLine, CartesianGrid, Legend } from 'recharts';
import PageTitle from '../layout/PageTitle';
import BlurredSection from '../ui/BlurredSection';
import UpgradeModal from '../ui/UpgradeModal';
import PriceLinkageCard from '../ui/PriceLinkageCard';
import { DataDelayNotice } from '../ui/DataDelayNotice';
import { formatSpikeReasons } from '../../utils/spikeReasons';
import { formatWssReason } from '../../utils/wssReasons';
import { trackUpgradeClick } from '../../utils/analytics';

interface TickerStats {
  ticker: string;
  total_mentions: number;
  avg_sentiment: number;
  bullish_predictions: number;
  bearish_predictions: number;
  neutral_predictions: number;
  unique_users: number;
  period_days?: number;
  hourly_data: Array<{
    hour: string;
    mentions: number;
    sentiment: number | null; // Может быть null для отсутствующих данных
    timestampUtc?: string | null; // UTC timestamp для правильного отображения локального времени
    priceClose?: number | null;
    priceOpen?: number | null;
    priceHigh?: number | null;
    priceLow?: number | null;
    priceVolume?: number | null;
    uniqueUsers?: number | null;
    discussionVolume?: number;
    discussionParticipants?: number;
  }>;
  sentiment_distribution: Array<{
    name: string;
    value: number;
    color: string;
  }>;
  recent_activity: Array<{
    content: string;
    sentiment: number;
    author: string;
    timestamp: string;
  }>;
}

const TickerDetails: React.FC = () => {
  const { ticker } = useParams<{ ticker: string }>();
  const navigate = useNavigate();
  const { isPro, loading: authLoading } = useAuth();
  const [stats, setStats] = useState<TickerStats | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState('overview');
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const [analyticsData, setAnalyticsData] = useState<any>(null);
  const [fullscreenChart, setFullscreenChart] = useState<string | null>(null);
  // Используем начальные значения на основе isPro, но не меняем их автоматически
  const [selectedPeriod, setSelectedPeriod] = useState<number>(() => isPro ? 24 : 12);
  const [selectedInterval, setSelectedInterval] = useState<number>(() => isPro ? 15 : 60);
  const isLoadingRef = useRef(false);
  const lastRequestRef = useRef<string>('');
  const requestTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const priceTooltipRef = useRef<HTMLDivElement>(null);
  const priceTooltipCloseTimerRef = useRef<NodeJS.Timeout | null>(null);
  const [priceTooltipOpen, setPriceTooltipOpen] = useState(false);
  const [priceTooltipRect, setPriceTooltipRect] = useState<DOMRect | null>(null);
  const [explainContributions, setExplainContributions] = useState<TickerContribution[] | null>(null);
  const [explainLoading, setExplainLoading] = useState(false);
  const [explainError, setExplainError] = useState<string | null>(null);
  const [spikeSignals, setSpikeSignals] = useState<SpikeSignal[] | null>(null);
  const [spikeLoading, setSpikeLoading] = useState(false);
  const [spikeError, setSpikeError] = useState<string | null>(null);
  const [fsShowDots, setFsShowDots] = useState(true);
  const [fsCurveMode, setFsCurveMode] = useState<'smooth' | 'step'>('smooth');
  const [fsNormalize, setFsNormalize] = useState(false);

  // Закрытие полноэкранного графика по Escape
  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setFullscreenChart(null);
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, []);

  // Блокировка скролла страницы при развёрнутом графике
  useEffect(() => {
    if (fullscreenChart) {
      const prev = document.body.style.overflow;
      document.body.style.overflow = 'hidden';
      return () => {
        document.body.style.overflow = prev;
      };
    }
  }, [fullscreenChart]);

  // После определения auth приводим селекторы к возможностям текущего тарифа.
  useEffect(() => {
    if (authLoading) return;
    setSelectedPeriod((prev) => {
      if (isPro) return prev === 12 ? 24 : prev;
      return 12;
    });
    setSelectedInterval((prev) => {
      if (isPro) return prev === 60 ? 15 : prev;
      return 60;
    });
  }, [isPro, authLoading]);

  // Синхронизация периода и интервала: 30d → только 1d; при периоде не 30d интервал 1d не годится → сбрасываем на 1h/15m
  useEffect(() => {
    if (selectedPeriod === 720 && selectedInterval !== 1440) {
      setSelectedInterval(1440);
    } else if (selectedPeriod !== 720 && selectedInterval === 1440) {
      setSelectedInterval(isPro ? 15 : 60);
    }
  }, [selectedPeriod, selectedInterval, isPro]);

  // Опции периода: FREE — только 12h; Premium — до 30 дней (720h)
  const PERIOD_OPTIONS = [12, 24, 72, 168, 720] as const; // 12h, 24h, 3d, 7d, 30d
  const INTERVAL_OPTIONS = [5, 15, 30, 60, 1440] as const; // 5m, 15m, 30m, 1h, 1d

  const canSelectPeriod = (hours: number) => isPro || hours === 12;
  // Для периода 30 дней разрешён только интервал 1 день
  const canSelectInterval = (minutes: number) => {
    if (selectedPeriod === 720) return isPro && minutes === 1440;
    return isPro || minutes === 60;
  };

  const formatIntervalLabel = (minutes: number) =>
    minutes >= 1440 ? '1d' : minutes >= 60 ? '1h' : `${minutes}m`;

  // Функция для форматирования периода в читаемый текст
  const formatPeriodText = (hours: number): string => {
    if (hours < 24) {
      return `Last ${hours} ${hours === 1 ? 'hour' : 'hours'}`;
    } else if (hours < 168) {
      const days = Math.round(hours / 24);
      return `Last ${days} ${days === 1 ? 'day' : 'days'}`;
    } else {
      const days = Math.round(hours / 24);
      return `Last ${days} days`;
    }
  };

  // Мемоизируем функцию loadTickerStats, чтобы она не создавалась заново при каждом рендере
  const loadTickerStats = useCallback(async () => {
    if (!ticker) return;
    
    // Предотвращаем параллельные запросы
    if (isLoadingRef.current) {
      return;
    }
    
    isLoadingRef.current = true;
    setIsLoading(true);
    setError(null);
    
    try {
      // Используем выбранные период и интервал согласно текущему тарифу.
      const hours = isPro ? selectedPeriod : Math.min(selectedPeriod, 12);
      // Для 30 дней бэкенду передаём только интервал 1 день (1440 мин)
      const interval = hours === 720 ? 1440 : (isPro ? selectedInterval : 60);
      
      // Запрашиваем данные за выбранный период
      const analyticsData = await apiService.getAnalytics(ticker, hours, interval, isPro);
      
      // Для метрик "24h" используем те же данные, если период >= 24, иначе используем все доступные данные
      const metrics24h = hours >= 24 ? analyticsData : analyticsData;
      
      // Функция для заполнения пропущенных часов на основе последней временной метки из API
      const fillMissingHours = (data: any[], hours: number = 24) => {
        const filledData = [];
        
        if (data.length === 0) {
          // Если данных нет, создаем пустые точки по часам
          const now = new Date();
          for (let i = hours - 1; i >= 0; i--) {
            const targetTime = new Date(now.getTime() - (i * 60 * 60 * 1000));
            filledData.push({
              hour: targetTime.toISOString(),
              timestampUtc: targetTime.toISOString(),
              mentions: 0,
              sentiment: 0,
              discussionVolume: 0,
              discussionParticipants: 0
            });
          }
          return filledData;
        }
        
        // Находим первую и последнюю временную метку из данных
        // API может возвращать timestamp или timestampUtc
        const timestamps = data.map(stat => {
          const ts = stat.timestampUtc || stat.timestamp;
          return new Date(ts).getTime();
        });
        const firstTimestamp = new Date(Math.min(...timestamps));
        const lastTimestamp = new Date(Math.max(...timestamps));
        
        // Создаем временные точки от первой до последней записи
        const totalHours = Math.ceil((lastTimestamp.getTime() - firstTimestamp.getTime()) / (60 * 60 * 1000)) + 1;
        
        for (let i = 0; i < totalHours; i++) {
          // Создаем время в UTC (используем UTC время из API)
          const targetTimeUtc = new Date(firstTimestamp.getTime() + (i * 60 * 60 * 1000));
          // Форматируем в локальное время пользователя для отображения
          const targetTime = new Date(targetTimeUtc);
          const targetHour = targetTime.toLocaleDateString('en-US', { month: '2-digit', day: '2-digit' }) + ', ' + targetTime.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: false });
          
          // Ищем данные для этого часа (сравниваем UTC время)
          const existingData = data.find(stat => {
            const ts = stat.timestampUtc || stat.timestamp;
            if (!ts) return false;
            const statDate = new Date(ts);
            // Сравниваем UTC время с точностью до часа
            const statHourUtc = statDate.getUTCHours();
            const targetHourUtc = targetTimeUtc.getUTCHours();
            const statDayUtc = statDate.getUTCDate();
            const targetDayUtc = targetTimeUtc.getUTCDate();
            const statMonthUtc = statDate.getUTCMonth();
            const targetMonthUtc = targetTimeUtc.getUTCMonth();
            const statYearUtc = statDate.getUTCFullYear();
            const targetYearUtc = targetTimeUtc.getUTCFullYear();
            
            return statHourUtc === targetHourUtc && 
                   statDayUtc === targetDayUtc && 
                   statMonthUtc === targetMonthUtc &&
                   statYearUtc === targetYearUtc;
          });
          
          if (existingData) {
            // API может возвращать sentiment или avgSentiment
            // Проверяем оба варианта и используем первый доступный
            // Важно: null - это отсутствие данных, не 0!
            let sentimentValue: number | null = null;
            const s = existingData.sentiment ?? existingData.avgSentiment;
            if (s !== null && s !== undefined && !isNaN(s)) {
              sentimentValue = s;
            }
            
            filledData.push({
              hour: targetHour,
              mentions: existingData.mentions || 0,
              sentiment: sentimentValue, // Может быть null
              priceClose: existingData.priceClose ?? null,
              priceOpen: existingData.priceOpen ?? null,
              priceHigh: existingData.priceHigh ?? null,
              priceLow: existingData.priceLow ?? null,
              priceVolume: existingData.priceVolume ?? null,
              uniqueUsers: existingData.uniqueUsers ?? existingData.totalUniqueUsers ?? null,
              discussionVolume: existingData.discussionVolume ?? 0,
              discussionParticipants: existingData.discussionParticipants ?? 0,
              timestampUtc: existingData.timestampUtc || existingData.timestamp || targetTimeUtc.toISOString()
            });
          } else {
            // Добавляем нулевые данные для пропущенного часа (mentions = 0, но sentiment тоже null)
            // Сохраняем timestampUtc для правильного отображения времени на графиках
            filledData.push({
              hour: targetHour,
              mentions: 0,
              sentiment: null as any, // Используем null для отсутствующих данных
              priceClose: null,
              priceOpen: null,
              priceHigh: null,
              priceLow: null,
              priceVolume: null,
              uniqueUsers: null,
              discussionVolume: 0,
              discussionParticipants: 0,
              timestampUtc: targetTimeUtc.toISOString() // Сохраняем UTC время для правильного отображения
            });
          }
        }
        

        
        return filledData;
      };

      // Агрегация timeSeries по одному дню (для интервала 1d: одна точка = один день)
      const aggregateByDay = (series: any[]): any[] => {
        const byDay = new Map<string, { mentions: number; sentimentSum: number; sentimentCount: number; priceClose: number | null; priceOpen: number | null; priceHigh: number | null; priceLow: number | null; priceVolume: number; uniqueUsers: number | null; discussionVolume: number; discussionParticipants: number; timestampUtc: string }>();
        for (const ts of series) {
          const d = new Date(ts.timestampUtc || ts.timestamp);
          const key = `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, '0')}-${String(d.getUTCDate()).padStart(2, '0')}`;
          if (!byDay.has(key)) {
            byDay.set(key, {
              mentions: 0,
              sentimentSum: 0,
              sentimentCount: 0,
              priceClose: null,
              priceOpen: null,
              priceHigh: null,
              priceLow: null,
              priceVolume: 0,
              uniqueUsers: null,
              discussionVolume: 0,
              discussionParticipants: 0,
              timestampUtc: new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate(), 12, 0, 0)).toISOString(),
            });
          }
          const row = byDay.get(key)!;
          row.mentions += ts.mentions ?? 0;
          const s = ts.sentiment ?? ts.avgSentiment;
          if (s != null && !isNaN(s)) {
            row.sentimentSum += s;
            row.sentimentCount += 1;
          }
          if (ts.priceClose != null) row.priceClose = ts.priceClose;
          if (ts.priceOpen != null) row.priceOpen = row.priceOpen ?? ts.priceOpen;
          if (ts.priceHigh != null) row.priceHigh = row.priceHigh == null ? ts.priceHigh : Math.max(row.priceHigh, ts.priceHigh);
          if (ts.priceLow != null) row.priceLow = row.priceLow == null ? ts.priceLow : Math.min(row.priceLow, ts.priceLow);
          row.priceVolume += ts.priceVolume ?? 0;
          if (ts.uniqueUsers != null || (ts as any).totalUniqueUsers != null) row.uniqueUsers = (ts.uniqueUsers ?? (ts as any).totalUniqueUsers) ?? row.uniqueUsers;
          row.discussionVolume += ts.discussionVolume ?? 0;
          row.discussionParticipants += ts.discussionParticipants ?? 0;
        }
        return Array.from(byDay.entries())
          .sort((a, b) => a[0].localeCompare(b[0]))
          .map(([, row]) => ({
            hour: row.timestampUtc,
            timestampUtc: row.timestampUtc,
            mentions: row.mentions,
            sentiment: row.sentimentCount > 0 ? row.sentimentSum / row.sentimentCount : null,
            priceClose: row.priceClose,
            priceOpen: row.priceOpen,
            priceHigh: row.priceHigh,
            priceLow: row.priceLow,
            priceVolume: row.priceVolume,
            uniqueUsers: row.uniqueUsers,
            discussionVolume: row.discussionVolume,
            discussionParticipants: row.discussionParticipants,
          }));
      };

      // Пустые точки по одному дню (для периода 30d при интервале 1d)
      const fillMissingDays = (days: number): any[] => {
        const result: any[] = [];
        const now = new Date();
        for (let i = days - 1; i >= 0; i--) {
          const target = new Date(now.getTime() - i * 24 * 60 * 60 * 1000);
          const timestampUtc = new Date(Date.UTC(target.getUTCFullYear(), target.getUTCMonth(), target.getUTCDate(), 12, 0, 0)).toISOString();
          result.push({
            hour: timestampUtc,
            timestampUtc,
            mentions: 0,
            sentiment: null,
            priceClose: null,
            priceOpen: null,
            priceHigh: null,
            priceLow: null,
            priceVolume: null,
            uniqueUsers: null,
            discussionVolume: 0,
            discussionParticipants: 0,
          });
        }
        return result;
      };
      
      // Убираем хвостовые точки с нулевыми mentions (неполный последний интервал с бэкенда даёт 0 и портит график)
      const trimTrailingZeroMentions = (arr: any[]): any[] => {
        if (arr.length <= 1) return arr;
        const out = [...arr];
        while (out.length > 1 && (out[out.length - 1].mentions ?? 0) === 0) out.pop();
        return out;
      };

      // Данные для графиков: при интервале 1 день (1440 мин) — одна точка на день; иначе по точкам API / fillMissingHours
      let tickerHourlyData: any[];
      const useDailyBuckets = interval === 1440;
      if (analyticsData.timeSeries && analyticsData.timeSeries.length > 0) {
        const mapped = analyticsData.timeSeries
          .map((ts: any) => {
            const tsUtc = ts.timestampUtc || ts.timestamp;
            const s = ts.sentiment ?? ts.avgSentiment;
            return {
              hour: tsUtc,
              timestampUtc: tsUtc,
              mentions: ts.mentions ?? 0,
              sentiment: s !== null && s !== undefined && !isNaN(s) ? s : null,
              priceClose: ts.priceClose ?? null,
              priceOpen: ts.priceOpen ?? null,
              priceHigh: ts.priceHigh ?? null,
              priceLow: ts.priceLow ?? null,
              priceVolume: ts.priceVolume ?? null,
              uniqueUsers: ts.uniqueUsers ?? ts.totalUniqueUsers ?? null,
              discussionVolume: ts.discussionVolume ?? 0,
              discussionParticipants: ts.discussionParticipants ?? 0,
            };
          })
          .sort((a: any, b: any) => new Date(a.timestampUtc).getTime() - new Date(b.timestampUtc).getTime());
        tickerHourlyData = trimTrailingZeroMentions(useDailyBuckets ? aggregateByDay(mapped) : mapped);
      } else {
        tickerHourlyData = useDailyBuckets ? fillMissingDays(Math.round(hours / 24)) : fillMissingHours([], hours);
      }
      
      // Используем данные за 24 часа для метрик "24h" из основного запроса
      // Если запросили меньше 24 часов, используем данные за запрошенный период
      const totalMentions24h = metrics24h?.overallStats?.totalMentions || 0;
      const avgSentiment24h = (metrics24h?.overallStats?.avgSentimentOverall ?? metrics24h?.overallStats?.avgSentiment) ?? 0;
      const uniqueUsers24h = metrics24h?.overallStats?.totalUniqueUsersAllTime ?? 
                             (metrics24h?.timeSeries && metrics24h.timeSeries.length > 0 
                               ? Math.max(...metrics24h.timeSeries.map((ts: any) => ts.uniqueUsers ?? ts.totalUniqueUsers ?? 0)) 
                               : 0);
      
      // Для Sentiment Distribution используем ТОЛЬКО данные с бэкенда
      // sentimentDistribution находится на верхнем уровне ответа (не в overallStats)
      const sentimentDist = analyticsData.sentimentDistribution || analyticsData.overallStats?.sentimentDistribution;
      
      // Используем данные из API без расчетов
      const bullishMentions = sentimentDist?.positiveMentions ?? 
                              sentimentDist?.bullish ?? 
                              analyticsData.overallStats?.positiveMentions ?? 
                              analyticsData.overallStats?.bullishMentions ?? 0;
      const bearishMentions = sentimentDist?.negativeMentions ?? 
                             sentimentDist?.bearish ?? 
                             analyticsData.overallStats?.negativeMentions ?? 
                             analyticsData.overallStats?.bearishMentions ?? 0;
      const neutralMentions = sentimentDist?.neutralMentions ?? 
                              sentimentDist?.neutral ?? 
                              analyticsData.overallStats?.neutralMentions ?? 0;
      
      // Используем данные из API как есть, без пересчетов
      const sentimentDistribution = [
        { name: 'Bullish', value: bullishMentions, color: '#10b981' },
        { name: 'Bearish', value: bearishMentions, color: '#ef4444' },
        { name: 'Neutral', value: neutralMentions, color: '#f59e0b' }
      ];
      
      // Создаем фиктивные данные для недавней активности (так как analytics API не возвращает posts/comments)
        const recentActivity = [
        { content: 'Looking bullish on this one!', sentiment: 0.8, author: 'trader123', timestamp: '2024-01-15T10:30:00Z' },
        { content: 'Not sure about this stock', sentiment: 0.1, author: 'investor456', timestamp: '2024-01-15T09:15:00Z' },
        { content: 'Great fundamentals', sentiment: 0.9, author: 'analyst789', timestamp: '2024-01-15T08:45:00Z' }
      ];
        
        const tickerStats: TickerStats = {
        ticker: ticker || '',
        total_mentions: totalMentions24h, // Используем данные из API как есть
        avg_sentiment: avgSentiment24h, // Используем данные за 24 часа для метрик
        bullish_predictions: bullishMentions,
        bearish_predictions: bearishMentions,
        neutral_predictions: neutralMentions,
        unique_users: uniqueUsers24h, // Используем данные за 24 часа для метрик
        period_days: 0.5, // 12 часов = 0.5 дня
          hourly_data: tickerHourlyData,
          sentiment_distribution: sentimentDistribution,
          recent_activity: recentActivity
        };
        
        setStats(tickerStats);
      // Сохраняем полные данные из API для использования в PRO секциях
      setAnalyticsData(analyticsData);
    } catch (error) {
      
      // Improved error handling
      if (error instanceof Error) {
        if (error.message.includes('404') || error.message.includes('not found')) {
          setError(`Ticker ${ticker?.toUpperCase()} not found. This ticker may not be in our database.`);
        } else if (error.message.includes('no data')) {
          setError(`No data available for the selected period for ticker ${ticker?.toUpperCase()}. Try selecting a different period.`);
        } else if (error.message.includes('429')) {
          setError('Too many requests. Please try again later.');
        } else if (error.message.includes('403') || error.message.includes('PRO')) {
          setError('This feature is only available for Premium subscribers.');
        } else {
          setError('Error loading data. Please try again later.');
        }
      } else {
        setError('Error loading data. Please try again later.');
      }
    } finally {
      setIsLoading(false);
      isLoadingRef.current = false;
    }
  }, [ticker, selectedPeriod, selectedInterval, isPro]); // Зависимости для useCallback

  // Выполняем запрос только при изменении ticker, selectedPeriod или selectedInterval (и после загрузки auth, чтобы PRO не получал free/analytics)
  useEffect(() => {
    if (!ticker || authLoading) return;
    
    // Очищаем предыдущий таймаут, если есть
    if (requestTimeoutRef.current) {
      clearTimeout(requestTimeoutRef.current);
    }
    
    // Создаем уникальный ключ запроса для предотвращения дублирования
    const requestKey = `${ticker}-${isPro ? 'pro' : 'free'}-${selectedPeriod}-${selectedInterval}`;
    
    // Пропускаем, если уже загружается тот же запрос
    if (isLoadingRef.current && lastRequestRef.current === requestKey) {
      return;
    }
    
    // Пропускаем, если это тот же запрос, что и последний успешный
    if (lastRequestRef.current === requestKey && !isLoadingRef.current) {
      return;
    }
    
    // Используем debounce для предотвращения множественных запросов при быстрых изменениях
    requestTimeoutRef.current = setTimeout(() => {
      lastRequestRef.current = requestKey;
      loadTickerStats();
    }, 100); // Небольшая задержка для debounce
    
    return () => {
      if (requestTimeoutRef.current) {
        clearTimeout(requestTimeoutRef.current);
      }
    };
  }, [ticker, isPro, selectedPeriod, selectedInterval, loadTickerStats, authLoading]);

  // Sentiment explanation (top contributors за последние 12h) — только для Premium
  useEffect(() => {
    if (!ticker || !isPro) {
      setExplainContributions(null);
      setExplainError(null);
      return;
    }
    let cancelled = false;
    setExplainLoading(true);
    setExplainError(null);
    apiService
      .getTickerExplain(ticker, 10)
      .then((data) => {
        if (!cancelled) {
          setExplainContributions(Array.isArray(data) ? data : []);
          setExplainError(null);
        }
      })
      .catch((err) => {
        if (!cancelled) {
          setExplainContributions(null);
          setExplainError(err?.message || 'Failed to load sentiment explanation.');
        }
      })
      .finally(() => {
        if (!cancelled) setExplainLoading(false);
      });
    return () => { cancelled = true; };
  }, [ticker, isPro]);

  // Spike detection по тикеру — период как в общем селекторе (только Premium).
  useEffect(() => {
    if (authLoading || !ticker || !isPro) {
      setSpikeSignals(null);
      setSpikeError(null);
      return;
    }
    // После auth-resolve у PRO selectedPeriod может кратко оставаться 12 до синхронизации.
    // Чтобы не делать лишний запрос на 12h, сразу используем минимум 24h для PRO.
    const hours = Math.max(selectedPeriod, 24);
    let cancelled = false;
    setSpikeLoading(true);
    setSpikeError(null);
    apiService
      .getSpikes(ticker, hours)
      .then((data) => {
        if (!cancelled) {
          setSpikeSignals(data.spikes || []);
          setSpikeError(null);
        }
      })
      .catch((err) => {
        if (!cancelled) {
          setSpikeSignals(null);
          setSpikeError(err?.message || 'Failed to load spike data.');
        }
      })
      .finally(() => {
        if (!cancelled) setSpikeLoading(false);
      });
    return () => { cancelled = true; };
  }, [ticker, isPro, selectedPeriod, authLoading]);

  const getSentimentColor = (sentiment: number) => {
    if (sentiment >= 0.2) return 'text-[#4ff6e6]';
    if (sentiment <= -0.2) return 'text-[#ff6b6b]';
    return 'text-[#D4AF37]';
  };

  const getSentimentIcon = (sentiment: number) => {
    if (sentiment >= 0.2) return <TrendingUp className="w-4 h-4 text-[#4ff6e6]" />;
    if (sentiment <= -0.2) return <TrendingDown className="w-4 h-4 text-[#ff6b6b]" />;
    return <Minus className="w-4 h-4 text-[#D4AF37]" />;
  };

  // Функция для форматирования времени в локальное время пользователя
  const formatLocalTime = (timestampUtc: string | null | undefined, fallbackLabel?: string | number): string => {
    if (timestampUtc) {
      const utcDate = new Date(timestampUtc);
      if (!isNaN(utcDate.getTime())) {
        // Автоматически конвертируется в локальное время браузера
        const time = utcDate.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit', hour12: false });
        const dateStr = utcDate.toLocaleDateString(undefined, { month: '2-digit', day: '2-digit' });
        return `${time} ${dateStr}`;
      }
    }
    return fallbackLabel ? String(fallbackLabel) : '';
  };

  // Формат подписи периода для заголовков графиков
  const formatPeriodLabel = (hours: number) =>
    hours < 24 ? `${hours}h` : `${Math.round(hours / 24)}d`;

  // Настройки осей графиков: интервал меток по X и форматтер, чтобы не налезали и читались
  const chartDataLength = stats?.hourly_data?.length ?? 0;
  const xAxisInterval = chartDataLength <= 12 ? 0 : Math.max(0, Math.floor((chartDataLength - 1) / 7));
  const showDots = chartDataLength <= 24;

  // Округление времени до границы выбранного интервала (чтобы не показывать 17:38, а 17:00 или 17:30)
  const roundToIntervalBoundary = (date: Date, intervalMinutes: number): Date => {
    const intervalMs = intervalMinutes * 60 * 1000;
    return new Date(Math.floor(date.getTime() / intervalMs) * intervalMs);
  };

  // Подписи по оси X — календарные даты, время округлено до границы интервала (17:00, 17:30, не 17:38)
  const chartXAxisTickFormatter = (value: string) => {
    const dataPoint = stats?.hourly_data?.find((d: any) => d.hour === value);
    const ts = dataPoint?.timestampUtc ?? value;
    const d = new Date(ts);
    if (isNaN(d.getTime())) return value;
    const rounded = roundToIntervalBoundary(d, selectedInterval);
    const dateStr = rounded.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
    if (selectedInterval >= 1440) {
      return dateStr;
    }
    const timeStr = rounded.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit', hour12: false });
    return dateStr + ', ' + timeStr;
  };

  const CHART_MARGIN = { top: 12, right: 24, left: 20, bottom: 48 };
  const fsLineType: 'monotone' | 'stepAfter' = fsCurveMode === 'step' ? 'stepAfter' : 'monotone';
  const fsDot = fsShowDots ? { r: 4 } : false;

  const fullscreenMentionsData = (() => {
    if (!stats?.hourly_data) return [];
    if (!fsNormalize) return stats.hourly_data;
    const maxMentions = Math.max(1, ...stats.hourly_data.map((d: any) => Number(d.mentions ?? 0)));
    return stats.hourly_data.map((d: any) => ({
      ...d,
      mentions: Math.round((Number(d.mentions ?? 0) / maxMentions) * 100),
    }));
  })();

  const fullscreenDiscussionData = (() => {
    if (!stats?.hourly_data) return [];
    if (!fsNormalize) return stats.hourly_data;
    const maxVolume = Math.max(1, ...stats.hourly_data.map((d: any) => Number(d.discussionVolume ?? 0)));
    const maxParticipants = Math.max(1, ...stats.hourly_data.map((d: any) => Number(d.discussionParticipants ?? 0)));
    return stats.hourly_data.map((d: any) => ({
      ...d,
      discussionVolume: Math.round((Number(d.discussionVolume ?? 0) / maxVolume) * 100),
      discussionParticipants: Math.round((Number(d.discussionParticipants ?? 0) / maxParticipants) * 100),
    }));
  })();

  const detailTabs = [
    { id: 'overview', label: 'Overview', icon: Activity, enabled: true },
    { id: 'charts', label: 'Time series', icon: BarChart3, enabled: true },
  ];

  const AnimatedLegend = ({ payload }: { payload?: any[] }) => {
    if (!payload || payload.length === 0) return null;
    return (
      <motion.div
        className="ticker-fs-legend"
        initial={{ opacity: 0, y: 6 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.25 }}
      >
        {payload.map((entry: any, index: number) => (
          <motion.span
            key={`${entry.value}-${index}`}
            className="ticker-fs-legend-item"
            style={{ borderColor: `${entry.color}66` }}
            initial={{ opacity: 0, y: 4 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.2, delay: index * 0.06 }}
          >
            <span className="ticker-fs-legend-dot" style={{ backgroundColor: entry.color }} />
            <span>{entry.value}</span>
          </motion.span>
        ))}
      </motion.div>
    );
  };

  const fullscreenInsight = (() => {
    if (!fullscreenChart || !stats?.hourly_data || stats.hourly_data.length === 0) return null;
    const rows = stats.hourly_data;
    const timeAt = (idx: number) => formatLocalTime(rows[idx]?.timestampUtc, rows[idx]?.hour || '');

    if (fullscreenChart === 'mentions-trend' || fullscreenChart === 'mentions-over-time') {
      const mentions = rows.map((r: any) => Number(r.mentions ?? 0));
      const first = mentions[0] ?? 0;
      const last = mentions[mentions.length - 1] ?? 0;
      const peak = Math.max(...mentions);
      const peakIdx = mentions.findIndex((v) => v === peak);
      const delta = last - first;
      const direction = delta > 0 ? 'up' : delta < 0 ? 'down' : 'flat';
      return {
        tone: direction === 'up' ? 'bull' : direction === 'down' ? 'bear' : 'neutral',
        text: `Peak buzz ${peak.toLocaleString()} at ${timeAt(peakIdx)}. Trend: ${direction} (${delta >= 0 ? '+' : ''}${delta.toLocaleString()}).`,
      };
    }

    if (fullscreenChart === 'discussion') {
      const volumes = rows.map((r: any) => Number(r.discussionVolume ?? 0));
      const participants = rows.map((r: any) => Number(r.discussionParticipants ?? 0));
      const peakVolume = Math.max(...volumes);
      const peakIdx = volumes.findIndex((v) => v === peakVolume);
      const latestParticipants = participants[participants.length - 1] ?? 0;
      return {
        tone: peakVolume > 0 ? 'bull' : 'neutral',
        text: `Peak thread volume ${peakVolume.toLocaleString()} at ${timeAt(peakIdx)}. Latest participants: ${latestParticipants.toLocaleString()}.`,
      };
    }

    if (fullscreenChart === 'sentiment') {
      const sentimentRows = rows.filter((r: any) => r.sentiment !== null && r.sentiment !== undefined);
      if (sentimentRows.length === 0) {
        return { tone: 'neutral', text: 'No sentiment signal yet for this period.' };
      }
      const avg = sentimentRows.reduce((acc: number, r: any) => acc + Number(r.sentiment), 0) / sentimentRows.length;
      const latest = Number(sentimentRows[sentimentRows.length - 1].sentiment);
      const tone = latest > 0.1 ? 'bull' : latest < -0.1 ? 'bear' : 'neutral';
      return {
        tone,
        text: `Latest sentiment ${(latest * 100).toFixed(1)}% • Average ${(avg * 100).toFixed(1)}% for selected period.`,
      };
    }

    return null;
  })();



  if (isLoading) {
    return (
      <div className="flex-1 flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#4ff6e6]"></div>
      </div>
    );
  }

  if (error || !stats) {
    const isNotFound = error?.includes('not found') || error?.includes('not found');
    
    return (
      <>
        <PageTitle title={isNotFound ? `${ticker?.toUpperCase()} - Not Found` : "Error"} />
        <div className="flex-1 flex items-center justify-center">
          <div className="text-center py-8 max-w-md mx-auto">
            <div className="mb-6">
              <div className="w-16 h-16 bg-[#ff6b6b]/10 rounded-full flex items-center justify-center mx-auto mb-4">
                <AlertCircle className="w-8 h-8 text-[#ff6b6b]" />
              </div>
              <h2 className="text-xl font-semibold text-white mb-2">
                {isNotFound ? 'Ticker Not Found' : 'Error Loading Data'}
              </h2>
              <p className="text-[#bbb] mb-4">{error}</p>
              {isNotFound && (
                <div className="bg-[#1a1a1a] border border-[#333] rounded-lg p-4 mb-4">
                  <p className="text-sm text-[#bbb]">
                    This ticker may not be in our database yet. 
                    Try searching for a different ticker or check back later.
                  </p>
                </div>
              )}
            </div>
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <button
                onClick={() => navigate('/dashboard')}
                className="btn btn-outline flex items-center justify-center"
              >
                <ArrowLeft className="w-4 h-4 mr-2" />
                Back to Dashboard
              </button>
              {!isNotFound && (
                <button
                  onClick={loadTickerStats}
                  className="btn btn-gold flex items-center justify-center"
                >
                  <RefreshCw className="w-4 h-4 mr-2" />
                  Retry
                </button>
              )}
            </div>
          </div>
        </div>
      </>
    );
  }

  return (
    <div className="content">
      <PageTitle title={stats.ticker} />

      {/* Main Content — без overflow, чтобы не перехватывать касания; скролл только у main */}
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 pt-8 max-w-6xl w-full max-w-full min-w-0 relative z-10">
        <div className="space-y-6 min-w-0">
          {/* Header */}

          <DataDelayNotice />
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
            <div className="flex items-center space-x-4">
              <button
                onClick={() => navigate(-1)}
                className="p-2 hover:bg-white/10 rounded-lg transition-colors text-white hover:bg-white/20"
              >
                <ArrowLeft className="w-5 h-5" />
              </button>
              <div>
                <h1 className="text-2xl font-bold text-white">${stats.ticker}</h1>
                <p className="text-white/80">
                  {isPro 
                    ? `Detailed sentiment analysis (${formatPeriodText(selectedPeriod)})`
                    : `Basic sentiment analysis (${formatPeriodText(selectedPeriod)} - FREE)`}
                </p>
              </div>
            </div>
            {!isPro && (
              <button
                onClick={() => {
                  trackUpgradeClick(`ticker_header:${stats.ticker}`);
                  setShowUpgradeModal(true);
                }}
                className="btn btn-gold w-full sm:w-auto"
              >
                Upgrade to Premium
              </button>
            )}
          </div>

          {/* Period & Interval — на мобильном два ряда (друг под другом), на sm+ одна строка */}
          <div className="ticker-toolbar-wrap period-interval-bar min-h-0 px-4 sm:px-5 py-4 mb-6 flex flex-col sm:flex-row sm:flex-nowrap sm:items-center gap-4 sm:gap-6 w-full max-w-full rounded-xl border overflow-x-visible sm:overflow-x-auto overflow-y-hidden" style={{ background: 'rgba(255,255,255,0.03)', borderColor: 'rgba(212, 175, 55, 0.2)' }}>
            {/* Time range — мобильный: лейбл сверху, кнопки в одну строку с горизонтальным скроллом при необходимости */}
            <div className="flex flex-col sm:flex-row sm:items-center gap-2 min-w-0 w-full sm:w-auto sm:shrink-0">
              <div className="flex items-center gap-2 shrink-0">
                <span className="text-xs font-medium uppercase tracking-wider text-brand-gold/90">Time range</span>
                {isPro && <span className="text-xs text-white/50">(up to 30 days)</span>}
              </div>
              <div className="ticker-toolbar-pill-group scroll-x-only min-h-0 flex rounded-lg overflow-hidden border bg-black/30 overflow-x-auto overflow-y-hidden" style={{ borderColor: 'rgba(212, 175, 55, 0.15)' }}>
                {PERIOD_OPTIONS.map((hours) => {
                  const enabled = canSelectPeriod(hours);
                  const isActive = selectedPeriod === hours;
                  return (
                    <button
                      key={hours}
                      type="button"
                      disabled={!enabled}
                      onClick={() => enabled && setSelectedPeriod(hours)}
                      title={!enabled ? 'Premium only — 30 days history' : undefined}
                      className={`ticker-toolbar-pill-btn px-3 sm:px-4 py-2 text-sm font-medium transition-colors border-r last:border-r-0 shrink-0 ${
                        !enabled
                          ? 'text-white/30 cursor-not-allowed bg-white/5 border-white/10 pointer-events-none'
                          : isActive
                            ? 'top-tickers-pill-btn-active text-black font-bold border-[#D4AF37]/30'
                            : 'text-white/80 hover:bg-white/10 hover:text-white border-white/10'
                      }`}
                      style={enabled && isActive ? { background: 'linear-gradient(135deg, #D4AF37 0%, #B8860B 100%)', boxShadow: '0 0 12px rgba(212, 175, 55, 0.35)' } : undefined}
                    >
                      {formatPeriodLabel(hours)}
                    </button>
                  );
                })}
              </div>
            </div>
            {/* Interval — то же для мобильного */}
            <div className="flex flex-col sm:flex-row sm:items-center gap-2 min-w-0 w-full sm:w-auto sm:shrink-0">
              <div className="flex items-center gap-2 shrink-0">
                <span className="text-xs font-medium uppercase tracking-wider text-brand-gold/90">Interval</span>
              </div>
              <div className="ticker-toolbar-pill-group scroll-x-only min-h-0 flex rounded-lg overflow-hidden border bg-black/30 overflow-x-auto overflow-y-hidden" style={{ borderColor: 'rgba(212, 175, 55, 0.15)' }}>
                {INTERVAL_OPTIONS.map((minutes) => {
                  const enabled = canSelectInterval(minutes);
                  const isActive = selectedInterval === minutes;
                  const intervalTitle = !enabled
                    ? selectedPeriod === 720
                      ? 'For 30 days only 1d interval'
                      : 'Premium only'
                    : undefined;
                  return (
                    <button
                      key={minutes}
                      type="button"
                      disabled={!enabled}
                      onClick={() => {
                      if (enabled) {
                        if (minutes === 1440) setSelectedPeriod(720);
                        setSelectedInterval(minutes);
                      }
                    }}
                      title={intervalTitle}
                      className={`ticker-toolbar-pill-btn px-3 sm:px-4 py-2 text-sm font-medium transition-colors border-r last:border-r-0 shrink-0 ${
                        !enabled
                          ? 'text-white/30 cursor-not-allowed bg-white/5 border-white/10 pointer-events-none'
                          : isActive
                            ? 'top-tickers-pill-btn-active text-black font-bold border-[#D4AF37]/30'
                            : 'text-white/80 hover:bg-white/10 hover:text-white border-white/10'
                      }`}
                      style={enabled && isActive ? { background: 'linear-gradient(135deg, #D4AF37 0%, #B8860B 100%)', boxShadow: '0 0 12px rgba(212, 175, 55, 0.35)' } : undefined}
                    >
                      {formatIntervalLabel(minutes)}
                    </button>
                  );
                })}
              </div>
            </div>
          </div>

          {/* Detail Navigation Tabs — min-h-0 и -mb-px у активного чтобы убрать микро-скролл от пары пикселей */}
          <div className="ticker-detail-tabs-wrap scroll-x-only min-h-0 border-b mb-6 relative z-10 overflow-x-auto overflow-y-hidden" style={{ borderColor: 'rgba(212, 175, 55, 0.2)' }}>
            <nav className="flex space-x-4 sm:space-x-8 min-w-max">
              {detailTabs.map((tab) => {
                const Icon = tab.icon;
                const isActive = activeTab === tab.id;
                return (
                  <button
                    key={tab.id}
                    onClick={() => tab.enabled && setActiveTab(tab.id)}
                    disabled={!tab.enabled}
                    style={{
                      color: !tab.enabled 
                        ? '#999' 
                        : isActive 
                        ? '#D4AF37' 
                        : '#ffffff',
                      borderBottomColor: isActive ? '#D4AF37' : 'transparent',
                      fontWeight: '600',
                      textShadow: isActive ? '0 0 10px rgba(212, 175, 55, 0.4)' : 'none'
                    }}
                    className={`flex items-center space-x-2 py-4 px-1 border-b-2 border-transparent text-sm transition-colors duration-200 relative z-10 ${
                      !tab.enabled
                        ? 'ticker-detail-tab ticker-detail-tab-disabled cursor-not-allowed opacity-50'
                        : isActive
                        ? 'ticker-detail-tab ticker-detail-tab-active border-[#D4AF37] -mb-px'
                        : 'ticker-detail-tab ticker-detail-tab-inactive hover:border-[#D4AF37]/60'
                    }`}
                  >
                    <Icon className={`w-4 h-4 ${isActive ? 'ticker-detail-tab-icon-active' : ''}`} style={{ color: 'inherit', filter: isActive ? 'drop-shadow(0 0 4px rgba(212, 175, 55, 0.6))' : 'none' }} />
                    <span style={{ color: 'inherit' }}>{tab.label}</span>
                  </button>
                );
              })}
            </nav>
          </div>

          {/* Content — без motion.div, чтобы скролл работал как на лендинге/дашборде (transform ломает скролл на мобильных) */}
          <div key={activeTab}>
            {activeTab === 'overview' && (
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 overflow-visible items-start content-start">
                {/* Key Metrics */}
                <div className="lg:col-span-2 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                  <div className="card-glass p-6">
                    <div className="flex items-center space-x-3">
                      <div className="p-2 bg-[#4ff6e6]/10 rounded-lg">
                        <MessageSquare className="w-5 h-5 text-[#4ff6e6]" />
                      </div>
                      <div className="flex-1">
                        <p className="text-sm text-[#bbb] mb-1">Total Mentions ({formatPeriodText(isPro ? selectedPeriod : Math.min(selectedPeriod, 12))})</p>
                        <p className="text-2xl font-bold text-white">{stats.total_mentions.toLocaleString()}</p>
                        {analyticsData?.overallStats?.avgMentionsPerPeriod && (
                          <p className="text-xs text-[#666] mt-1">
                            Avg: {analyticsData.overallStats.avgMentionsPerPeriod.toFixed(1)}/period
                          </p>
                        )}
                      </div>
                    </div>
                  </div>

                  <div className="card-glass p-6">
                    <div className="flex items-center space-x-3">
                      <div className="p-2 bg-[#4ff6e6]/10 rounded-lg">
                        <Users className="w-5 h-5 text-[#4ff6e6]" />
                      </div>
                      <div className="flex-1">
                        <p className="text-sm text-[#bbb] mb-1">Unique Users ({formatPeriodText(isPro ? selectedPeriod : Math.min(selectedPeriod, 12))})</p>
                        <p className="text-2xl font-bold text-white">{stats.unique_users.toLocaleString()}</p>
                        {analyticsData?.overallStats?.avgUniqueUsersPerPeriod && (
                          <p className="text-xs text-[#666] mt-1">
                            Avg: {analyticsData.overallStats.avgUniqueUsersPerPeriod.toFixed(1)}/period
                          </p>
                        )}
                      </div>
                    </div>
                  </div>

                  <div className="card-glass p-6">
                    <div className="flex items-center space-x-3">
                      <div className="p-2 bg-[#4ff6e6]/10 rounded-lg">
                        <Activity className="w-5 h-5 text-[#4ff6e6]" />
                      </div>
                      <div className="flex-1">
                        <p className="text-sm text-[#bbb] mb-1">Avg Sentiment ({formatPeriodText(isPro ? selectedPeriod : Math.min(selectedPeriod, 12))})</p>
                        <div className="flex items-center space-x-2">
                          <p className="text-2xl font-bold text-white">
                            {Math.round((stats.avg_sentiment ?? 0) * 100)}%
                          </p>
                          {getSentimentIcon(stats.avg_sentiment ?? 0)}
                        </div>
                        <p className={`text-xs mt-1 ${getSentimentColor(stats.avg_sentiment ?? 0)}`}>
                          {(stats.avg_sentiment ?? 0).toFixed(3)}
                        </p>
                      </div>
                    </div>
                  </div>

                  {/* Price Card - если есть данные о цене */}
                  {analyticsData?.timeSeries && analyticsData.timeSeries.length > 0 && 
                   analyticsData.timeSeries.some((ts: any) => ts.priceClose !== null && ts.priceClose !== undefined) ? (
                    <div className="card-glass p-6 overflow-visible">
                      <div className="flex items-center space-x-3">
                        <div className="p-2 bg-[#4ff6e6]/10 rounded-lg">
                          <DollarSign className="w-5 h-5 text-[#4ff6e6]" />
                        </div>
                        <div className="flex-1 overflow-visible">
                          <div className="flex items-center gap-1.5 mb-1">
                            <p className="text-sm text-[#bbb]">Last Available Price</p>
                            <div
                              ref={priceTooltipRef}
                              className="inline-flex"
                              onMouseEnter={() => {
                                if (priceTooltipCloseTimerRef.current) {
                                  clearTimeout(priceTooltipCloseTimerRef.current);
                                  priceTooltipCloseTimerRef.current = null;
                                }
                                if (priceTooltipRef.current) {
                                  setPriceTooltipRect(priceTooltipRef.current.getBoundingClientRect());
                                  setPriceTooltipOpen(true);
                                }
                              }}
                              onMouseLeave={() => {
                                priceTooltipCloseTimerRef.current = setTimeout(() => {
                                  setPriceTooltipOpen(false);
                                  setPriceTooltipRect(null);
                                  priceTooltipCloseTimerRef.current = null;
                                }, 100);
                              }}
                            >
                              <HelpCircle className="w-4 h-4 text-[#666] cursor-help hover:text-[#4ff6e6] transition-colors shrink-0" />
                            </div>
                            {priceTooltipOpen && priceTooltipRect && createPortal(
                              <div
                                className="fixed w-56 bg-[#0d0d0d] border border-[#333] rounded-lg p-2 text-xs text-white shadow-xl z-[9999] pointer-events-none"
                                style={{
                                  left: priceTooltipRect.left + priceTooltipRect.width / 2,
                                  top: priceTooltipRect.top - 8,
                                  transform: 'translate(-50%, -100%)',
                                }}
                              >
                                Last price we successfully obtained from our data. May not reflect the live market price.
                              </div>,
                              document.body
                            )}
                          </div>
                          {(() => {
                            const latestPrice = analyticsData.timeSeries
                              .filter((ts: any) => ts.priceClose !== null && ts.priceClose !== undefined)
                              .slice(-1)[0]?.priceClose;
                            const prevPrice = analyticsData.timeSeries
                              .filter((ts: any) => ts.priceClose !== null && ts.priceClose !== undefined)
                              .slice(-2)[0]?.priceClose;
                            const change = latestPrice && prevPrice ? latestPrice - prevPrice : null;
                            const changePercent = change && prevPrice ? (change / prevPrice) * 100 : null;
                            return (
                              <>
                                <p className="text-2xl font-bold text-white">
                                  ${latestPrice?.toFixed(2) || 'N/A'}
                                </p>
                                {change !== null && changePercent !== null && (
                                  <p className={`text-xs mt-1 ${change >= 0 ? 'text-[#4ff6e6]' : 'text-[#ff6b6b]'}`}>
                                    {change >= 0 ? '+' : ''}{change.toFixed(2)} ({changePercent >= 0 ? '+' : ''}{changePercent.toFixed(2)}%)
                                  </p>
                                )}
                              </>
                            );
                          })()}
                        </div>
                      </div>
                    </div>
                  ) : (
                    <div className="card-glass p-6">
                      <div className="flex items-center space-x-3">
                        <div className="p-2 bg-[#4ff6e6]/10 rounded-lg">
                          <DollarSign className="w-5 h-5 text-[#4ff6e6]" />
                        </div>
                        <div className="flex-1">
                          <p className="text-sm text-[#bbb] mb-1">Sentiment Score</p>
                          <p className={`text-2xl font-bold ${getSentimentColor(stats.avg_sentiment ?? 0)}`}>
                            {(stats.avg_sentiment ?? 0).toFixed(3)}
                          </p>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Discussion in threads */}
                  <div className="card-glass p-6">
                    <div className="flex items-center space-x-3">
                      <div className="p-2 bg-[#4ff6e6]/10 rounded-lg">
                        <BarChart3 className="w-5 h-5 text-[#4ff6e6]" />
                      </div>
                      <div className="flex-1">
                        <p className="text-sm text-[#bbb] mb-1">Discussion Volume</p>
                        <p className="text-2xl font-bold text-white">
                          {(analyticsData?.overallStats?.discussionVolume ?? 0).toLocaleString()}
                        </p>
                        <p className="text-xs text-[#666] mt-1">
                          Posts + comments in threads
                        </p>
                      </div>
                    </div>
                  </div>

                  <div className="card-glass p-6">
                    <div className="flex items-center space-x-3">
                      <div className="p-2 bg-[#4ff6e6]/10 rounded-lg">
                        <Activity className="w-5 h-5 text-[#4ff6e6]" />
                      </div>
                      <div className="flex-1">
                        <p className="text-sm text-[#bbb] mb-1">Discussion Participants</p>
                        <p className="text-2xl font-bold text-white">
                          {(analyticsData?.overallStats?.discussionParticipants ?? 0).toLocaleString()}
                        </p>
                        <p className="text-xs text-[#666] mt-1">
                          Unique participants in threads
                        </p>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Spike detection — слева от Quick Stats, одна высота с Quick Stats */}
                <div className="h-full flex flex-col min-h-0">
                  <BlurredSection isPro={isPro} onUpgrade={() => setShowUpgradeModal(true)} title="Spike detection - Premium">
                    <div className="card-glass p-6 h-full min-h-[420px] flex flex-col">
                      <h3 className="text-lg font-semibold text-white mb-2 flex items-center gap-2">
                        <Flame className="w-5 h-5 text-orange-400" />
                        Spike detection (last {formatPeriodLabel(selectedPeriod)})
                      </h3>
                      <p className="text-sm text-[#bbb] mb-4">
                        Interest spikes in 5-min windows: mentions and unique users above baseline. Levels: NONE → WATCH → HOT → VIRAL.
                      </p>
                      {!isPro ? (
                        <div className="bg-[#0d0d0d] border border-[#333] rounded-lg p-8 text-center">
                          <p className="text-white/60 text-sm">Available with Premium. Upgrade to see spike detection.</p>
                        </div>
                      ) : (
                        <>
                          {spikeLoading && (
                            <div className="flex items-center justify-center py-8">
                              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#4ff6e6]" />
                            </div>
                          )}
                          {!spikeLoading && spikeError && (
                            <div className="bg-[#0d0d0d] border border-[#333] rounded-lg p-4 text-center">
                              <p className="text-[#ff6b6b] text-sm">{spikeError}</p>
                            </div>
                          )}
                          {!spikeLoading && !spikeError && spikeSignals && spikeSignals.length === 0 && (
                            <div className="bg-[#0d0d0d] border border-[#333] rounded-lg p-4 text-center">
                              <p className="text-white/60 text-sm">No spikes for this ticker in the selected period.</p>
                            </div>
                          )}
                          {!spikeLoading && !spikeError && spikeSignals && spikeSignals.length > 0 && (() => {
                            const elevated = spikeSignals.filter((s) => s.level && s.level !== 'NONE');
                            if (elevated.length === 0) {
                              return (
                                <div className="bg-[#0d0d0d] border border-[#333] rounded-lg p-4 text-center">
                                  <p className="text-white/60 text-sm">No elevated spike levels in the selected period.</p>
                                </div>
                              );
                            }
                            return (
                              <div className="space-y-3 pr-1 max-h-[280px] overflow-y-auto">
                                {elevated.map((signal, index) => {
                                  const levelStyle =
                                    signal.level === 'VIRAL'
                                      ? { bg: 'rgba(239, 68, 68, 0.2)', color: '#ef4444', border: 'rgba(239, 68, 68, 0.4)' }
                                      : signal.level === 'HOT'
                                      ? { bg: 'rgba(249, 115, 22, 0.2)', color: '#f97316', border: 'rgba(249, 115, 22, 0.4)' }
                                      : signal.level === 'WATCH'
                                      ? { bg: 'rgba(212, 175, 55, 0.2)', color: '#D4AF37', border: 'rgba(212, 175, 55, 0.4)' }
                                      : { bg: 'rgba(255,255,255,0.08)', color: '#aaa', border: 'rgba(255,255,255,0.15)' };
                                  return (
                                    <div
                                      key={index}
                                      className="bg-[#0d0d0d] border border-[#333] rounded-lg p-4 flex flex-col gap-2"
                                    >
                                      <div className="flex flex-wrap items-center gap-2">
                                        <span
                                          className={`text-xs font-bold uppercase px-2 py-1 rounded spike-level-badge spike-level-badge--${signal.level?.toLowerCase?.() || 'none'}`}
                                          style={{ background: levelStyle.bg, color: levelStyle.color, border: `1px solid ${levelStyle.border}` }}
                                        >
                                          {signal.level}
                                        </span>
                                        <span className="text-sm text-[#D4AF37] font-medium">Score: {typeof signal.score === 'number' ? signal.score.toFixed(1) : signal.score}</span>
                                        {signal.window_metrics && (
                                          <span className="text-xs text-[#888]">
                                            {signal.window_metrics.mentions} mentions · {signal.window_metrics.unique_users} users
                                          </span>
                                        )}
                                      </div>
                                      {signal.reasons && signal.reasons.length > 0 && (
                                        <p className="text-xs text-[#bbb] leading-relaxed">
                                          {formatSpikeReasons(signal.reasons)}
                                        </p>
                                      )}
                                      {signal.window_start && (
                                        <span className="text-xs text-[#666]">
                                          {new Date(signal.window_start).toLocaleString(undefined, { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })}
                                        </span>
                                      )}
                                    </div>
                                  );
                                })}
                              </div>
                            );
                          })()}
                        </>
                      )}
                    </div>
                  </BlurredSection>
                </div>

                {/* Quick Stats — справа от Spike detection, одна высота со Spike detection */}
                <div className="card-glass card-tooltips p-6 h-full flex flex-col min-h-0 relative z-20">
                  <h3 className="text-lg font-semibold text-white mb-4 shrink-0">Quick Stats</h3>
                  <div className="space-y-3 flex-1 min-h-0 overflow-visible">
                    <div className="flex justify-between items-center group relative">
                      <div className="flex items-center space-x-2">
                      <span className="text-[#bbb]">Bullish Ratio</span>
                        <div className="relative">
                          <HelpCircle className="w-4 h-4 text-[#666] cursor-help hover:text-[#4ff6e6] transition-colors" />
                          <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 w-48 bg-[#0d0d0d] border border-[#333] rounded-lg p-2 text-xs text-white opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all pointer-events-none z-[9999] shadow-lg">
                            Percentage of mentions with positive sentiment (sentiment &gt; 0.1)
                          </div>
                        </div>
                      </div>
                      <span className="font-semibold text-white">
                        {analyticsData?.sentimentDistribution?.positivePercentage !== undefined 
                          ? analyticsData.sentimentDistribution.positivePercentage.toFixed(1) 
                          : 'N/A'}%
                      </span>
                    </div>
                    <div className="flex justify-between items-center group relative">
                      <div className="flex items-center space-x-2">
                      <span className="text-[#bbb]">Bearish Ratio</span>
                        <div className="relative">
                          <HelpCircle className="w-4 h-4 text-[#666] cursor-help hover:text-[#4ff6e6] transition-colors" />
                          <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 w-48 bg-[#0d0d0d] border border-[#333] rounded-lg p-2 text-xs text-white opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all pointer-events-none z-[9999] shadow-lg">
                            Percentage of mentions with negative sentiment (sentiment &lt; -0.1)
                          </div>
                        </div>
                      </div>
                      <span className="font-semibold text-white">
                        {analyticsData?.sentimentDistribution?.negativePercentage !== undefined 
                          ? analyticsData.sentimentDistribution.negativePercentage.toFixed(1) 
                          : 'N/A'}%
                      </span>
                    </div>
                    <div className="flex justify-between items-center group relative">
                      <div className="flex items-center space-x-2">
                      <span className="text-[#bbb]">Mentions per User</span>
                        <div className="relative">
                          <HelpCircle className="w-4 h-4 text-[#666] cursor-help hover:text-[#4ff6e6] transition-colors" />
                          <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 w-48 bg-[#0d0d0d] border border-[#333] rounded-lg p-2 text-xs text-white opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all pointer-events-none z-[9999] shadow-lg">
                            Average number of mentions per unique user. Higher values indicate more active discussion.
                          </div>
                        </div>
                      </div>
                      <span className="font-semibold text-white">
                        {analyticsData?.overallStats?.mentionsPerUser !== undefined 
                          ? analyticsData.overallStats.mentionsPerUser.toFixed(1) 
                          : 'N/A'}
                      </span>
                    </div>
                    <div className="flex justify-between items-center group relative">
                      <div className="flex items-center space-x-2">
                        <span className="text-[#bbb]">Discussion Volume</span>
                        <div className="relative">
                          <HelpCircle className="w-4 h-4 text-[#666] cursor-help hover:text-[#4ff6e6] transition-colors" />
                          <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 w-56 bg-[#0d0d0d] border border-[#333] rounded-lg p-2 text-xs text-white opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all pointer-events-none z-[9999] shadow-lg">
                            Posts with ticker + all comments in those threads (thread discussion volume).
                          </div>
                        </div>
                      </div>
                      <span className="font-semibold text-white">
                        {(analyticsData?.overallStats?.discussionVolume ?? 0).toLocaleString()}
                      </span>
                    </div>
                    <div className="flex justify-between items-center group relative">
                      <div className="flex items-center space-x-2">
                        <span className="text-[#bbb]">Discussion Participants</span>
                        <div className="relative">
                          <HelpCircle className="w-4 h-4 text-[#666] cursor-help hover:text-[#4ff6e6] transition-colors" />
                          <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 w-56 bg-[#0d0d0d] border border-[#333] rounded-lg p-2 text-xs text-white opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all pointer-events-none z-[9999] shadow-lg">
                            Unique participants in threads about this ticker (posts + comments).
                          </div>
                        </div>
                      </div>
                      <span className="font-semibold text-white">
                        {(analyticsData?.overallStats?.discussionParticipants ?? 0).toLocaleString()}
                      </span>
                    </div>
                    {analyticsData?.overallStats?.sentimentVolatility !== undefined && (
                      <div className="flex justify-between items-center group relative">
                        <div className="flex items-center space-x-2">
                          <span className="text-[#bbb]">Sentiment Volatility</span>
                          <div className="relative">
                            <HelpCircle className="w-4 h-4 text-[#666] cursor-help hover:text-[#4ff6e6] transition-colors" />
                            <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 w-56 bg-[#0d0d0d] border border-[#333] rounded-lg p-2 text-xs text-white opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all pointer-events-none z-[9999] shadow-lg">
                              Measures how much sentiment values fluctuate over time. Higher volatility means more unpredictable sentiment changes.
                            </div>
                          </div>
                        </div>
                        <span className="font-semibold text-white">
                          {(analyticsData.overallStats.sentimentVolatility * 100).toFixed(1)}%
                        </span>
                      </div>
                    )}
                    {analyticsData?.overallStats?.mentionsSentimentCorrelation !== undefined && (
                      <div className="flex justify-between items-center group relative">
                        <div className="flex items-center space-x-2">
                          <span className="text-[#bbb]">Correlation</span>
                          <div className="relative">
                            <HelpCircle className="w-4 h-4 text-[#666] cursor-help hover:text-[#4ff6e6] transition-colors" />
                            <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 w-64 bg-[#0d0d0d] border border-[#333] rounded-lg p-2 text-xs text-white opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all pointer-events-none z-[9999] shadow-lg">
                              Correlation between mention volume and sentiment. Values range from -1 to 1. Positive values indicate that more mentions correlate with more positive sentiment.
                            </div>
                          </div>
                        </div>
                        <span className="font-semibold text-white">
                          {analyticsData.overallStats.mentionsSentimentCorrelation.toFixed(3)}
                        </span>
                      </div>
                    )}
                    {analyticsData?.overallStats?.maxMentions !== undefined && (
                      <div className="flex justify-between items-center group relative">
                        <div className="flex items-center space-x-2">
                          <span className="text-[#bbb]">Peak Mentions</span>
                          <div className="relative">
                            <HelpCircle className="w-4 h-4 text-[#666] cursor-help hover:text-[#4ff6e6] transition-colors" />
                            <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 w-48 bg-[#0d0d0d] border border-[#333] rounded-lg p-2 text-xs text-white opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all pointer-events-none z-[9999] shadow-lg">
                              Maximum number of mentions in a single time period during the analyzed timeframe.
                            </div>
                          </div>
                        </div>
                        <span className="font-semibold text-white">
                          {analyticsData.overallStats.maxMentions}
                        </span>
                      </div>
                    )}
                  </div>
                </div>

                <div>
                  <BlurredSection isPro={isPro} onUpgrade={() => setShowUpgradeModal(true)} title="Price Linkage - Premium">
                    <PriceLinkageCard
                      ticker={ticker || ''}
                      sessionType="REGULAR"
                      windowDays={60}
                      enabled={isPro}
                    />
                  </BlurredSection>
                </div>

                {/* Sentiment Distribution — справа от Mentions Trend, одна высота */}
                <div className="card-glass card-tooltips p-6 rounded-xl h-full flex flex-col min-h-0">
                  <h3 className="text-lg font-semibold text-white mb-4 shrink-0">Sentiment Distribution{!isPro && <span className="text-brand-gold/80 font-normal ml-1">· 6h delay</span>}</h3>
                  <div className="flex-1 min-h-0 flex flex-col">
                  {(stats.total_mentions ?? 0) === 0 ? (
                    <div className="bg-[#0d0d0d] border border-[#333] rounded-lg p-6 text-center">
                      <p className="text-white/80 mb-1">No data for this ticker yet.</p>
                      <p className="text-sm text-[#666]">Try another ticker or check back later.</p>
                    </div>
                  ) : (
                    <>
                      <div className="grid grid-cols-3 gap-4 mb-4">
                        <div className="ticker-sentiment-stat text-center p-4 rounded-lg border bg-black/20" style={{ borderColor: 'rgba(255,255,255,0.1)' }}>
                          <div className="text-3xl font-bold text-emerald-400 mb-1">{stats.bullish_predictions}</div>
                          <div className="text-sm text-white/70">Bullish</div>
                          <div className="text-xs text-white/50 mt-1">
                            {analyticsData?.sentimentDistribution?.positivePercentage !== undefined 
                              ? analyticsData.sentimentDistribution.positivePercentage.toFixed(1) 
                              : 'N/A'}%
                          </div>
                        </div>
                        <div className="ticker-sentiment-stat text-center p-4 rounded-lg border bg-black/20" style={{ borderColor: 'rgba(255,255,255,0.1)' }}>
                          <div className="text-3xl font-bold text-red-400 mb-1">{stats.bearish_predictions}</div>
                          <div className="text-sm text-white/70">Bearish</div>
                          <div className="text-xs text-white/50 mt-1">
                            {analyticsData?.sentimentDistribution?.negativePercentage !== undefined 
                              ? analyticsData.sentimentDistribution.negativePercentage.toFixed(1) 
                              : 'N/A'}%
                          </div>
                        </div>
                        <div className="ticker-sentiment-stat text-center p-4 rounded-lg border bg-black/20" style={{ borderColor: 'rgba(255,255,255,0.1)' }}>
                          <div className="text-3xl font-bold text-brand-gold mb-1">{stats.neutral_predictions}</div>
                          <div className="text-sm text-white/70">Neutral</div>
                          <div className="text-xs text-white/50 mt-1">
                            {analyticsData?.sentimentDistribution?.neutralPercentage !== undefined 
                              ? analyticsData.sentimentDistribution.neutralPercentage.toFixed(1) 
                              : 'N/A'}%
                          </div>
                        </div>
                      </div>
                      {/* Pie Chart */}
                      {stats.sentiment_distribution && stats.sentiment_distribution.length > 0 && stats.sentiment_distribution.some((e: { value: number }) => e.value > 0) && (
                        <div className="chart-touch-passthrough h-48 overflow-visible min-h-0">
                          <ResponsiveContainer width="100%" height="100%">
                            <PieChart margin={{ top: 20, right: 36, bottom: 20, left: 36 }}>
                              <Pie
                                data={stats.sentiment_distribution}
                                cx="50%"
                                cy="50%"
                                labelLine={false}
                                label={({ name, percent }) => `${name}: ${((percent ?? 0) * 100).toFixed(0)}%`}
                                outerRadius={68}
                                fill="#8884d8"
                                dataKey="value"
                              >
                                {stats.sentiment_distribution.map((entry: { color: string }, index: number) => (
                                  <Cell key={`cell-${index}`} fill={entry.color} />
                                ))}
                              </Pie>
                              <Tooltip
                                contentStyle={{
                                  backgroundColor: 'rgba(16, 16, 20, 0.96)',
                                  border: '1px solid rgba(255,255,255,0.14)',
                                  borderRadius: '10px',
                                  color: '#fff',
                                  padding: '10px 12px',
                                  boxShadow: '0 10px 24px rgba(0,0,0,0.45)',
                                }}
                                content={({ active, payload }) => {
                                  if (!active || !payload?.length || !stats.sentiment_distribution) return null;
                                  const total = (stats.sentiment_distribution as { value: number }[]).reduce((s, e) => s + e.value, 0);
                                  const item = payload[0];
                                  const name = item?.name ?? '';
                                  const value = Number(item?.value ?? 0);
                                  const percent = total ? ((value / total) * 100).toFixed(0) : '0';
                                  const color = (item?.payload as { color?: string })?.color ?? '#bbb';
                                  return (
                                    <span style={{ color, fontWeight: 600 }}>{name}: {percent}%</span>
                                  );
                                }}
                              />
                            </PieChart>
                          </ResponsiveContainer>
                        </div>
                      )}
                    </>
                  )}
                  </div>
                </div>
              </div>
            )}

            {activeTab === 'charts' && (
              <div className="space-y-6 overflow-visible">
                {/* FREE: Базовые графики */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 overflow-visible">
                {/* Mentions Over Time */}
                <div className="card-glass card-tooltips p-6 relative">
                  <button
                    type="button"
                    onClick={() => setFullscreenChart('mentions-over-time')}
                    className="absolute top-4 right-4 p-2 rounded-lg bg-white/10 hover:bg-white/20 text-white/80 hover:text-white transition-colors z-10"
                    title="Full screen"
                    aria-label="Full screen"
                  >
                    <Maximize2 className="w-4 h-4" />
                  </button>
                  <h3 className="text-lg font-semibold text-white mb-2 pr-10">
                    Mentions Over Time (last {formatPeriodLabel(selectedPeriod)}){!isPro && <span className="text-brand-gold/80 font-normal ml-1">· 6h delay</span>}
                  </h3>
                  <div className="flex items-start gap-2 mb-4">
                    <p className="text-sm text-[#bbb] flex-1">
                      <strong className="text-white/80">Count of explicit ticker mentions</strong> (e.g. $SPY, SPY) per time bucket in Reddit posts and comments. A spike = more people are talking about the ticker; a drop = fading interest. Unlike Discussion volume, this counts each mention separately — one post with 10 mentions = 10.
                    </p>
                    <div className="relative group shrink-0">
                      <HelpCircle className="w-4 h-4 text-[#666] cursor-help hover:text-[#4ff6e6] transition-colors" />
                      <div className="absolute top-full right-0 mt-2 w-72 max-w-[min(288px,calc(100vw-2rem))] bg-[#0d0d0d] border border-[#333] rounded-lg p-2.5 text-xs text-white opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all pointer-events-none z-[9999] shadow-lg">
                        We detect explicit ticker symbols in text (e.g. $AAPL, AAPL, GME). Each occurrence counts as one mention. One comment with &quot;$TSLA $TSLA&quot; = 2 mentions. Useful for tracking buzz intensity over time.
                      </div>
                    </div>
                  </div>
                  <div className="chart-touch-passthrough ticker-chart-wrap-sm" style={{ width: '100%', height: 300 }}>
                  <ResponsiveContainer width="100%" height={300}>
                    <LineChart data={stats.hourly_data} margin={CHART_MARGIN}>
                      <XAxis dataKey="hour" stroke="#bbb" tick={{ fontSize: 10, fill: '#bbb' }} interval={xAxisInterval} tickFormatter={chartXAxisTickFormatter} angle={-40} textAnchor="end" height={56} />
                      <YAxis stroke="#bbb" tick={{ fontSize: 10, fill: '#bbb' }} width={44} allowDecimals={false} />
                      <Tooltip
                        content={({ active, payload, label }) => {
                          if (active && payload && payload.length) {
                            const dataIndex = payload[0]?.payload ? stats.hourly_data.findIndex((d: any) => d.hour === label) : -1;
                            const dataPoint = dataIndex >= 0 ? stats.hourly_data[dataIndex] : null;
                            const timeLabel = formatLocalTime(dataPoint?.timestampUtc, typeof label === 'string' || typeof label === 'number' ? label : undefined);
                            return (
                              <div className="bg-[#1a1a1a] border border-[#333] rounded-lg p-3 shadow-lg">
                                <p className="text-white font-medium mb-2">{timeLabel}</p>
                                <p className="text-[#4ff6e6] text-sm">Mentions: {Number(payload[0]?.value ?? 0).toLocaleString()}</p>
                              </div>
                            );
                          }
                          return null;
                        }}
                      />
                      <Line type="monotone" dataKey="mentions" stroke="#4ff6e6" strokeWidth={2} dot={showDots ? { fill: '#4ff6e6', strokeWidth: 2, r: 3 } : false} activeDot={{ r: 5, stroke: '#4ff6e6', strokeWidth: 2 }} />
                    </LineChart>
                  </ResponsiveContainer>
                  </div>
                </div>

                {/* Discussion Over Time */}
                <div className="card-glass card-tooltips p-6 relative">
                  <button
                    type="button"
                    onClick={() => setFullscreenChart('discussion')}
                    className="absolute top-4 right-4 p-2 rounded-lg bg-white/10 hover:bg-white/20 text-white/80 hover:text-white transition-colors z-10"
                    title="Full screen"
                    aria-label="Full screen"
                  >
                    <Maximize2 className="w-4 h-4" />
                  </button>
                  <h3 className="text-lg font-semibold text-white mb-2 pr-10">
                    Discussion in Threads (last {formatPeriodLabel(selectedPeriod)}){!isPro && <span className="text-brand-gold/80 font-normal ml-1">· 6h delay</span>}
                  </h3>
                  <div className="flex items-start gap-2 mb-4">
                    <p className="text-sm text-[#bbb] flex-1">
                      <strong className="text-white/80">Volume</strong> (teal): total posts and comments in Reddit threads that mention this ticker. <strong className="text-white/80">Participants</strong> (gold): unique users who wrote those posts or comments. High volume with many participants = broad engagement; a few users with many comments = echo chamber.
                    </p>
                    <div className="relative group shrink-0">
                      <HelpCircle className="w-4 h-4 text-[#666] cursor-help hover:text-[#4ff6e6] transition-colors" />
                      <div className="absolute top-full right-0 mt-2 w-72 max-w-[min(288px,calc(100vw-2rem))] bg-[#0d0d0d] border border-[#333] rounded-lg p-2.5 text-xs text-white opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all pointer-events-none z-[9999] shadow-lg">
                        A &quot;thread&quot; is a Reddit post plus all its comments. We count only threads where the ticker is explicitly mentioned. Volume shows total activity; participants show how widely the discussion is spread across users.
                      </div>
                    </div>
                  </div>
                  <div className="chart-touch-passthrough ticker-chart-wrap-sm" style={{ width: '100%', height: 300 }}>
                  <ResponsiveContainer width="100%" height={300}>
                    <LineChart data={stats.hourly_data} margin={CHART_MARGIN}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#333" opacity={0.5} />
                      <XAxis dataKey="hour" stroke="#bbb" tick={{ fontSize: 10, fill: '#bbb' }} interval={xAxisInterval} tickFormatter={chartXAxisTickFormatter} angle={-40} textAnchor="end" height={56} />
                      <YAxis stroke="#bbb" tick={{ fontSize: 10, fill: '#bbb' }} width={44} allowDecimals={false} />
                      <Tooltip
                        content={({ active, payload, label }) => {
                          if (active && payload && payload.length) {
                            const dataIndex = payload[0]?.payload ? stats.hourly_data.findIndex((d: any) => d.hour === label) : -1;
                            const dataPoint = dataIndex >= 0 ? stats.hourly_data[dataIndex] : null;
                            const timeLabel = formatLocalTime(dataPoint?.timestampUtc, typeof label === 'string' || typeof label === 'number' ? label : undefined);
                            return (
                              <div className="bg-[#1a1a1a] border border-[#333] rounded-lg p-3 shadow-lg">
                                <p className="text-white font-medium mb-2">{timeLabel}</p>
                                <p className="text-[#4ff6e6] text-sm">Volume: {Number(payload.find(p => p.dataKey === 'discussionVolume')?.value ?? 0).toLocaleString()}</p>
                                <p className="text-[#D4AF37] text-sm">Participants: {Number(payload.find(p => p.dataKey === 'discussionParticipants')?.value ?? 0).toLocaleString()}</p>
                              </div>
                            );
                          }
                          return null;
                        }}
                      />
                      <Legend wrapperStyle={{ fontSize: 11 }} formatter={(value) => <span className="text-[#bbb]">{value}</span>} />
                      <Line type="monotone" dataKey="discussionVolume" name="Volume" stroke="#4ff6e6" strokeWidth={2} dot={showDots ? { fill: '#4ff6e6', r: 3 } : false} connectNulls activeDot={{ r: 5 }} />
                      <Line type="monotone" dataKey="discussionParticipants" name="Participants" stroke="#D4AF37" strokeWidth={2} dot={showDots ? { fill: '#D4AF37', r: 3 } : false} connectNulls activeDot={{ r: 5 }} />
                    </LineChart>
                  </ResponsiveContainer>
                  </div>
                </div>

                {/* WSS Analysis - Always visible if data available */}
                {analyticsData?.wssAnalysis && (
                  <div className="card-glass card-tooltips p-6">
                    <h3 className="text-lg font-semibold text-white mb-2">WSS Analysis (Weighted Sentiment Score){!isPro && <span className="text-brand-gold/80 font-normal ml-1">· 6h delay</span>}</h3>
                    <p className="text-sm text-[#bbb] mb-4">
                      Accurate sentiment assessment with weighted scores.
                    </p>
                    {(() => {
                      const wss = analyticsData.wssAnalysis;
                      const noData = (wss.wssReason === 'insufficient_data' || (wss.wssScore === 0 && (wss.baselineTotalMentions ?? 0) === 0));
                      const hasNoTickerData = (analyticsData?.overallStats?.totalMentions ?? 0) === 0 && (analyticsData?.timeSeries?.length ?? 0) === 0;
                      // API может возвращать zscore или zScore
                      const zScore = wss.zscore ?? wss.zScore ?? null;
                      const spikePeak = wss.spikePeakHourMentions ?? wss.spikePeak ?? wss.spikePeakHour ?? (wss.spikeAvg ? Math.round(wss.spikeAvg) : null) ?? null;
                      
                      if (noData) {
                        return (
                          <div className="bg-[#0d0d0d] border border-[#333] rounded-lg p-6 text-center">
                            <div className="text-[#D4AF37] text-2xl font-bold mb-2">—</div>
                            <p className="text-white/80 mb-1">WSS is not calculated yet</p>
                            <p className="text-sm text-[#666]">
                              {hasNoTickerData
                                ? 'No data for this ticker yet. Try another ticker or check back later.'
                                : `Backend returned: ${formatWssReason(wss.wssReason || 'insufficient_data') || 'insufficient data'}. Try a longer period (24h/72h) or Premium for full analysis.`}
                            </p>
                          </div>
                        );
                      }
                      
                      const wssScoreTip = 'Composite metric: mention growth vs baseline, volume, sentiment, and user factor (tanh). Can be negative when mentions drop (declining interest), even if sentiment is positive.';
                      const directionTip = 'Based only on average sentiment in the spike window: positive → BULLISH, negative → BEARISH, else NEUTRAL. So you can have BULLISH tone but negative WSS if interest is declining.';
                      return (
                        <div className="space-y-4">
                          <div className="grid grid-cols-2 gap-4">
                            <div className="bg-[#0d0d0d] border border-[#333] rounded-lg p-4 relative group">
                              <div className="text-xs text-[#bbb] mb-1 flex items-center gap-1">
                                WSS Score
                                <div className="relative">
                                  <HelpCircle className="w-3.5 h-3.5 text-[#666] cursor-help hover:text-[#4ff6e6] transition-colors" />
                                  <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 w-56 bg-[#0d0d0d] border border-[#333] rounded-lg p-2.5 text-xs text-white opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all pointer-events-none z-[9999] shadow-lg">
                                    {wssScoreTip}
                                  </div>
                                </div>
                              </div>
                              <div className={`text-lg font-bold leading-tight ${(wss.wssScore ?? 0) > 0.7 ? 'text-[#4ff6e6]' : (wss.wssScore ?? 0) > 0.4 ? 'text-[#D4AF37]' : 'text-[#ff6b6b]'}`}>
                                {((wss.wssScore ?? 0) * 100).toFixed(1)}%
                              </div>
                            </div>
                            <div className="bg-[#0d0d0d] border border-[#333] rounded-lg p-4 relative group">
                              <div className="text-xs text-[#bbb] mb-1 flex items-center gap-1">
                                Sentiment
                                <div className="relative">
                                  <HelpCircle className="w-3.5 h-3.5 text-[#666] cursor-help hover:text-[#4ff6e6] transition-colors" />
                                  <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 w-56 bg-[#0d0d0d] border border-[#333] rounded-lg p-2.5 text-xs text-white opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all pointer-events-none z-[9999] shadow-lg">
                                    {directionTip}
                                  </div>
                                </div>
                              </div>
                              <div className={`text-lg font-bold leading-tight ${
                                wss.direction === 'bullish' ? 'text-[#4ff6e6]' : 
                                wss.direction === 'bearish' ? 'text-[#ff6b6b]' : 
                                'text-[#D4AF37]'
                              }`}>
                                {wss.direction?.toUpperCase() || 'NEUTRAL'}
                                {(wss.confidence != null && wss.confidence > 0) && (
                                  <span className="text-sm font-medium text-white/70 ml-1">
                                    ({(wss.confidence * 100).toFixed(0)}%)
                                  </span>
                                )}
                              </div>
                            </div>
                          </div>
                          {wss.wssReason && (
                            <div className="bg-[#0d0d0d] border border-[#333] rounded-lg p-3">
                              <div className="text-xs text-[#666] mb-1">Volume trend</div>
                              <div className="text-sm text-white/90">{formatWssReason(wss.wssReason)}</div>
                            </div>
                          )}
                          <div className="bg-[#0d0d0d] border border-[#333] rounded-lg p-4">
                            <div className="text-sm text-[#bbb] mb-3">Analysis Details</div>
                            <div className="space-y-2 text-sm">
                              <div className="flex justify-between">
                                <span className="text-[#bbb]">Confidence:</span>
                                <span className="text-white font-medium">{((wss.confidence ?? 0) * 100).toFixed(0)}%</span>
                              </div>
                              <div className="flex justify-between">
                                <span className="text-[#bbb]">Z-Score:</span>
                                <span className="text-white font-medium">
                                  {zScore !== null && zScore !== undefined && zScore !== 0 ? zScore.toFixed(2) : 
                                   (wss.zscore !== null && wss.zscore !== undefined && wss.zscore !== 0 ? wss.zscore.toFixed(2) : 
                                    (wss.zScore !== null && wss.zScore !== undefined && wss.zScore !== 0 ? wss.zScore.toFixed(2) : 'N/A'))}
                                </span>
                              </div>
                              <div className="flex justify-between">
                                <span className="text-[#bbb]">Relative Growth:</span>
                                <span className="text-white font-medium">
                                  {wss.relativeGrowth !== null && wss.relativeGrowth !== undefined && wss.relativeGrowth !== 0
                                    ? (wss.relativeGrowth * 100).toFixed(1) + '%'
                                    : 'N/A'}
                                </span>
                              </div>
                              <div className="flex justify-between">
                                <span className="text-[#bbb]">Spike Peak (last 4h):</span>
                                <span className="text-white font-medium">
                                  {(wss.spikePeakHourMentions !== null && wss.spikePeakHourMentions !== undefined && wss.spikePeakHourMentions > 0)
                                    ? `${wss.spikePeakHourMentions} mentions` 
                                    : (spikePeak !== null && spikePeak !== undefined && spikePeak > 0 
                                      ? `${spikePeak} mentions` 
                                      : 'N/A')}
                                </span>
                              </div>
                              <div className="flex justify-between">
                                <span className="text-[#bbb]">Peak (period):</span>
                                <span className="text-white font-medium">
                                  {wss.periodPeakMentions !== null && wss.periodPeakMentions !== undefined && wss.periodPeakMentions > 0
                                    ? `${wss.periodPeakMentions} mentions`
                                    : 'N/A'}
                                </span>
                              </div>
                            </div>
                          </div>
                        </div>
                      );
                    })()}
                  </div>
                )}

                {/* Sentiment explanation — только Premium; для FREE размытый блок + Upgrade */}
                <BlurredSection isPro={isPro} onUpgrade={() => setShowUpgradeModal(true)} title="Sentiment explanation - Premium">
                  <div className="card-glass p-6">
                    <h3 className="text-lg font-semibold text-white mb-2">Sentiment explanation</h3>
                    <p className="text-sm text-[#bbb] mb-4">
                      Top posts and comments that contributed most to the overall sentiment for the selected period.
                    </p>
                    {!isPro ? (
                      <div className="bg-black/20 border border-white/10 rounded-lg p-4 sm:p-6 min-h-[240px] sm:min-h-[280px] flex flex-col">
                        <div className="space-y-3">
                          <div className="h-4 w-40 rounded bg-white/15" />
                          <div className="h-3 w-full rounded bg-white/10" />
                          <div className="h-3 w-11/12 rounded bg-white/10" />
                        </div>
                        <div className="mt-4 space-y-2">
                          <div className="h-16 rounded-lg bg-black/25 border border-white/10" />
                          <div className="h-16 rounded-lg bg-black/25 border border-white/10" />
                          <div className="h-16 rounded-lg bg-black/25 border border-white/10" />
                        </div>
                        <p className="mt-auto pt-4 text-white/65 text-sm text-center">
                          Available with Premium. Upgrade to see top contributors.
                        </p>
                      </div>
                    ) : (
                      <>
                    {explainLoading && (
                      <div className="flex items-center justify-center py-8">
                        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#4ff6e6]" />
                      </div>
                    )}
                    {!explainLoading && explainError && (
                      <div className="bg-[#0d0d0d] border border-[#333] rounded-lg p-4 text-center">
                        <p className="text-[#ff6b6b] text-sm">{explainError}</p>
                      </div>
                    )}
                    {!explainLoading && !explainError && explainContributions && explainContributions.length === 0 && (
                      <div className="bg-[#0d0d0d] border border-[#333] rounded-lg p-4 text-center">
                        <p className="text-white/60 text-sm">No contribution data for this period.</p>
                      </div>
                    )}
                    {!explainLoading && !explainError && explainContributions && explainContributions.length > 0 && (() => {
                      const sentiment = (item: TickerContribution) => item.score ?? item.sentiment ?? 0;
                      const text = (item: TickerContribution) => (item.textPreview ?? item.title ?? item.body ?? '').trim();
                      const meaningful = explainContributions.filter(
                        (item) => sentiment(item) !== 0 || (item.weightedScore ?? item.contribution ?? 0) !== 0 || text(item).length > 0
                      );
                      if (meaningful.length === 0) {
                        return (
                          <div className="bg-[#0d0d0d] border border-[#333] rounded-lg p-4 text-center">
                            <p className="text-white/60 text-sm">No meaningful contribution data for this period.</p>
                          </div>
                        );
                      }
                      return (
                        <div className="space-y-3 pr-1 max-h-96 overflow-y-auto">
                          {meaningful.map((item, index) => {
                            const snippet = text(item);
                            const displaySnippet = snippet.length > 280 ? snippet.slice(0, 280) + '…' : snippet;
                            const scoreVal = sentiment(item);
                            const sentimentPct = (scoreVal * 100).toFixed(0);
                            const weighted = item.weightedScore ?? item.contribution ?? 0;
                            const weightedLabel = typeof item.weightedScore === 'number' ? `Weighted: ${weighted.toFixed(2)}` : `Contribution: ${(weighted * 100).toFixed(1)}%`;
                            const subredditUrl = item.subreddit ? `https://www.reddit.com/r/${item.subreddit}/` : null;
                            const postUrl = (item.redditId || item.redditPostId) && item.type === 'POST'
                              ? `https://www.reddit.com/comments/${item.redditId || item.redditPostId}/`
                              : null;
                            const createdLabel = item.createdUtc
                              ? new Date(item.createdUtc).toLocaleString(undefined, { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })
                              : null;
                            const engagement = item.ups ?? item.redditScore ?? item.numComments;
                            return (
                              <div
                                key={index}
                                className="bg-[#0d0d0d] border border-[#333] rounded-lg p-4 flex flex-col gap-2"
                              >
                                <div className="flex flex-wrap items-center gap-2 text-xs text-[#666]">
                                  {item.type && <span className="uppercase font-medium">{item.type}</span>}
                                  {item.subreddit && (
                                    subredditUrl ? (
                                      <a href={subredditUrl} target="_blank" rel="noopener noreferrer" className="text-brand-gold hover:underline">r/{item.subreddit}</a>
                                    ) : (
                                      <span>r/{item.subreddit}</span>
                                    )
                                  )}
                                  {item.author && <span>u/{item.author}</span>}
                                  {createdLabel && <span>{createdLabel}</span>}
                                  {engagement != null && engagement > 0 && <span>↑ {engagement}</span>}
                                </div>
                                {displaySnippet && <p className="text-sm text-white/90 line-clamp-4">{displaySnippet}</p>}
                                <div className="flex flex-wrap items-center gap-2 text-xs">
                                  <span className={`font-medium ${getSentimentColor(scoreVal)}`}>
                                    Sentiment: {sentimentPct}%
                                  </span>
                                  <span className="text-[#666]">{weightedLabel}</span>
                                  {postUrl && (
                                    <a href={postUrl} target="_blank" rel="noopener noreferrer" className="text-brand-gold hover:underline">
                                      View on Reddit →
                                    </a>
                                  )}
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      );
                    })()}
                      </>
                    )}
                  </div>
                </BlurredSection>

                {/* Sentiment Over Time */}
                <div className="card-glass p-6 relative">
                  <button
                    type="button"
                    onClick={() => setFullscreenChart('sentiment')}
                    className="absolute top-4 right-4 p-2 rounded-lg bg-white/10 hover:bg-white/20 text-white/80 hover:text-white transition-colors z-10"
                    title="Full screen"
                    aria-label="Full screen"
                  >
                    <Maximize2 className="w-4 h-4" />
                  </button>
                  <h3 className="text-lg font-semibold text-white mb-4 pr-10">
                    Sentiment Over Time (last {formatPeriodLabel(selectedPeriod)}){!isPro && <span className="text-brand-gold/80 font-normal ml-1">· 6h delay</span>}
                  </h3>
                  {stats.hourly_data && stats.hourly_data.length > 0 ? (
                  <div className="chart-touch-passthrough ticker-chart-wrap-lg" style={{ width: '100%', height: 350 }}>
                  <ResponsiveContainer width="100%" height={350}>
                    <AreaChart data={stats.hourly_data.map((d: any) => ({
                      ...d,
                      positiveSentiment: d.sentiment !== null && d.sentiment !== undefined && d.sentiment > 0 ? d.sentiment : null,
                      negativeSentiment: d.sentiment !== null && d.sentiment !== undefined && d.sentiment < 0 ? d.sentiment : null,
                    }))} margin={CHART_MARGIN}>
                      <defs>
                        <linearGradient id="colorPositive" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="5%" stopColor="#10b981" stopOpacity={0.8}/>
                          <stop offset="95%" stopColor="#10b981" stopOpacity={0.05}/>
                        </linearGradient>
                        <linearGradient id="colorNegative" x1="0" y1="1" x2="0" y2="0">
                          <stop offset="5%" stopColor="#ef4444" stopOpacity={0.8}/>
                          <stop offset="95%" stopColor="#ef4444" stopOpacity={0.05}/>
                        </linearGradient>
                      </defs>
                      <CartesianGrid strokeDasharray="3 3" stroke="#333" opacity={0.3} />
                      <XAxis dataKey="hour" stroke="#bbb" tick={{ fontSize: 10, fill: '#bbb' }} interval={xAxisInterval} tickFormatter={chartXAxisTickFormatter} angle={-40} textAnchor="end" height={56} />
                      <YAxis stroke="#bbb" tick={{ fontSize: 10, fill: '#bbb' }} width={44} domain={[-1, 1]} tickFormatter={(value) => `${(value * 100).toFixed(0)}%`} />
                      <Tooltip 
                        content={({ active, payload, label }) => {
                          if (active && payload && payload.length) {
                            const sentiment = payload.find((p: any) => p.dataKey === 'sentiment')?.value as number;
                            // Находим timestampUtc из данных для правильного отображения локального времени
                            const dataIndex = payload[0]?.payload ? stats.hourly_data.findIndex((d: any) => d.hour === label) : -1;
                            const dataPoint = dataIndex >= 0 ? stats.hourly_data[dataIndex] : null;
                            const timeLabel = formatLocalTime(dataPoint?.timestampUtc, typeof label === 'string' || typeof label === 'number' ? label : undefined);
                            
                            return (
                              <div className="bg-[#1a1a1a] border border-[#333] rounded-lg p-3 shadow-lg">
                                <p className="text-white font-medium mb-2">{timeLabel}</p>
                                <p className={`text-sm font-semibold ${
                                  sentiment !== null && sentiment !== undefined
                                    ? sentiment > 0.1
                                      ? 'text-emerald-500'
                                      : sentiment < -0.1
                                        ? 'text-red-500'
                                        : 'text-amber-500'
                                    : 'text-[#bbb]'
                                }`}>
                                    Sentiment: {sentiment !== null && sentiment !== undefined 
                                      ? `${(sentiment * 100).toFixed(1)}%`
                                      : 'N/A'}
                                </p>
                                {sentiment !== null && sentiment !== undefined && (
                                  <p className="text-xs text-[#666] mt-1">
                                    {sentiment > 0.1 ? '🟢 Bullish' : sentiment < -0.1 ? '🔴 Bearish' : '🟡 Neutral'}
                                  </p>
                                )}
                              </div>
                            );
                          }
                          return null;
                        }}
                      />
                      {/* Референсная линия на нуле */}
                      <ReferenceLine y={0} stroke="#666" strokeDasharray="2 2" strokeWidth={1.5} />
                      
                      {/* Область для положительного сентимента (выше нуля) */}
                      <Area
                        type="monotone"
                        dataKey="positiveSentiment"
                        stroke="#10b981"
                        strokeWidth={3}
                        fill="url(#colorPositive)"
                        connectNulls={false}
                        isAnimationActive={true}
                      />
                      
                      {/* Область для отрицательного сентимента (ниже нуля) */}
                      <Area
                        type="monotone"
                        dataKey="negativeSentiment"
                        stroke="#ef4444"
                        strokeWidth={3}
                        fill="url(#colorNegative)"
                        connectNulls={false}
                        isAnimationActive={true}
                      />
                      
                      {/* Основная линия сентимента с цветными точками */}
                      <Line 
                        type="monotone" 
                        dataKey="sentiment" 
                        stroke="#4ff6e6" 
                        strokeWidth={2}
                        dot={(props: any) => {
                          const { cx, cy, payload, index } = props;
                          const key = payload?.timestampUtc ?? payload?.hour ?? index ?? 0;
                          if (payload.sentiment === null || payload.sentiment === undefined) {
                            return <circle key={key} cx={cx} cy={cy} r={0} fill="transparent" />;
                          }
                          const color = payload.sentiment > 0.1 ? '#10b981' : payload.sentiment < -0.1 ? '#ef4444' : '#f59e0b';
                          return <circle key={key} cx={cx} cy={cy} r={4} fill={color} stroke="#fff" strokeWidth={2} />;
                        }}
                        activeDot={(props: any) => {
                          const { cx, cy, payload, index } = props;
                          const key = payload?.timestampUtc ?? payload?.hour ?? index ?? 0;
                          const color = payload.sentiment !== null && payload.sentiment !== undefined
                            ? (payload.sentiment > 0.1 ? '#10b981' : payload.sentiment < -0.1 ? '#ef4444' : '#f59e0b')
                            : '#4ff6e6';
                          return <circle key={key} cx={cx} cy={cy} r={6} fill={color} stroke="#fff" strokeWidth={2} />;
                        }}
                        connectNulls={false}
                      />
                    </AreaChart>
                  </ResponsiveContainer>
                  </div>
                  ) : (
                    <div className="h-64 card-glass rounded-lg flex items-center justify-center border border-white/10">
                      <p className="text-white/50">No sentiment data available</p>
                    </div>
                  )}
                </div>

                </div>

                {/* PRO: Детальные графики с разными интервалами */}
                {/* PRO: Subreddit Breakdown */}
                <div className="w-full">
                  <BlurredSection
                    isPro={isPro}
                    onUpgrade={() => setShowUpgradeModal(true)}
                    title="Subreddit Breakdown - Premium"
                  >
                    <div className="card-glass w-full max-w-none p-6">
                      <h3 className="text-lg font-semibold text-white mb-4">Subreddit Breakdown</h3>
                      <p className="text-white/70 mb-4">
                        See which subreddit discusses this ticker the most.
                      </p>
                      {analyticsData?.subredditBreakdown && analyticsData.subredditBreakdown.length > 0 ? (
                        <div className="space-y-3 max-h-[430px] overflow-y-auto pr-1">
                          {analyticsData.subredditBreakdown.slice(0, 10).map((sub: any, index: number) => (
                            <div key={index} className="bg-black/20 border border-white/10 rounded-lg p-4">
                              <div className="flex items-center justify-between mb-2">
                                <div className="flex items-center space-x-2">
                                  <span className="text-white font-semibold">r/{sub.subreddit}</span>
                                  <span className="text-xs text-white/75 bg-white/10 px-2 py-1 rounded">
                                    {sub.percentageOfTotal !== undefined 
                                      ? sub.percentageOfTotal.toFixed(1) 
                                      : 'N/A'}%
                                  </span>
                                </div>
                                <span className={`text-sm font-medium ${(sub.avgSentiment ?? 0) > 0.2 ? 'text-[#4ff6e6]' : (sub.avgSentiment ?? 0) < -0.2 ? 'text-[#ff6b6b]' : 'text-[#D4AF37]'}`}>
                                  {((sub.avgSentiment ?? 0) * 100).toFixed(1)}%
                                </span>
                              </div>
                              <div className="flex items-center justify-between text-sm text-white/70">
                                <span>{sub.totalMentions || sub.mentions} mentions</span>
                                <span>{sub.totalUniqueUsers || 0} users</span>
                              </div>
                              <div className="mt-2 h-2 bg-white/10 rounded-full overflow-hidden">
                                <div 
                                  className="h-full bg-[#4ff6e6]" 
                                  style={{ width: `${sub.percentageOfTotal || ((sub.totalMentions / analyticsData.overallStats.totalMentions) * 100)}%` }}
                                />
                              </div>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <div className="h-64 bg-[#0d0d0d] rounded-lg flex items-center justify-center border border-[#333]">
                          <p className="text-[#666]">No subreddit data available</p>
                        </div>
                      )}
                    </div>
                  </BlurredSection>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Expanded chart overlay — описание, легенда, стиль SentiHype */}
      <AnimatePresence>
        {fullscreenChart && (
          <>
            <motion.div
              className="fixed inset-0 z-[1100] bg-black/70 backdrop-blur-md pointer-events-auto"
              role="dialog"
              aria-modal="true"
              aria-label="Chart expanded"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.2 }}
              onClick={() => setFullscreenChart(null)}
            />
            <motion.div
              className="ticker-fullscreen-modal fixed z-[1101] top-20 left-4 right-4 bottom-4 sm:top-24 sm:left-6 sm:right-6 sm:bottom-6 md:top-28 md:left-8 md:right-8 md:bottom-8 lg:top-32 lg:left-12 lg:right-12 lg:bottom-12 flex flex-col rounded-2xl overflow-hidden shadow-2xl"
              style={{ background: 'rgba(15,15,15,0.98)', border: '1px solid rgba(212, 175, 55, 0.15)' }}
              initial={{ opacity: 0, scale: 0.92 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.92 }}
              transition={{ type: 'tween', duration: 0.25, ease: [0.25, 0.46, 0.45, 0.94] }}
              onClick={(e) => e.stopPropagation()}
            >
              <div className="ticker-fullscreen-modal-header flex items-center justify-between gap-4 px-4 py-3 border-b shrink-0" style={{ borderColor: 'rgba(212, 175, 55, 0.2)' }}>
                <div className="flex-1 min-w-0">
                  <h3 className="text-lg font-semibold text-brand-gold">
                    {fullscreenChart === 'mentions-trend' && `Mentions Trend (last ${formatPeriodLabel(selectedPeriod)})${!isPro ? ' · 6h delay' : ''}`}
                    {fullscreenChart === 'mentions-over-time' && `Mentions Over Time (last ${formatPeriodLabel(selectedPeriod)})${!isPro ? ' · 6h delay' : ''}`}
                    {fullscreenChart === 'discussion' && `Discussion in Threads (last ${formatPeriodLabel(selectedPeriod)})${!isPro ? ' · 6h delay' : ''}`}
                    {fullscreenChart === 'sentiment' && `Sentiment Over Time (last ${formatPeriodLabel(selectedPeriod)})${!isPro ? ' · 6h delay' : ''}`}
                  </h3>
                  <p className="text-sm text-white/60 mt-1 w-full">
                    {fullscreenChart === 'mentions-trend' && 'Number of explicit ticker mentions per time bucket (e.g. $TICKER or TICKER). Shows how much attention the ticker gets.'}
                    {fullscreenChart === 'mentions-over-time' && 'Count of explicit ticker mentions (e.g. $SPY, SPY) per time bucket. A spike = more buzz; a drop = fading interest. Each mention counts separately — one post with 10 mentions = 10.'}
                    {fullscreenChart === 'discussion' && 'Volume: total posts and comments in Reddit threads that mention this ticker. Participants: unique users who wrote them. High volume + many participants = broad engagement; few users with many comments = echo chamber.'}
                    {fullscreenChart === 'sentiment' && 'Average sentiment from −100% (bearish) to +100% (bullish). Green = positive, red = negative. Not investment advice.'}
                  </p>
                  <div className="ticker-fs-meta-row mt-3 flex flex-wrap items-center gap-2">
                    <span className="ticker-fs-chip ticker-fs-chip--gold">Interactive</span>
                    <span className="ticker-fs-chip ticker-fs-chip--glass">Period: {formatPeriodLabel(selectedPeriod)}</span>
                    {!isPro && <span className="ticker-fs-chip ticker-fs-chip--warn">FREE · 6h delay</span>}
                    <span className="ticker-fs-chip ticker-fs-chip--glass">Ticker: ${stats.ticker}</span>
                  </div>
                  {fullscreenInsight && (
                    <div className={`ticker-fs-insight mt-2 ticker-fs-insight--${fullscreenInsight.tone}`}>
                      <span className="ticker-fs-insight-label">Insight</span>
                      <span>{fullscreenInsight.text}</span>
                    </div>
                  )}
                  <div className="ticker-fs-controls mt-3">
                    <button
                      type="button"
                      onClick={() => setFsCurveMode((prev) => (prev === 'smooth' ? 'step' : 'smooth'))}
                      className={`ticker-fs-control-btn ${fsCurveMode === 'smooth' ? 'is-active' : ''}`}
                    >
                      {fsCurveMode === 'smooth' ? 'Smooth' : 'Step'}
                    </button>
                    <button
                      type="button"
                      onClick={() => setFsShowDots((prev) => !prev)}
                      className={`ticker-fs-control-btn ${fsShowDots ? 'is-active' : ''}`}
                    >
                      Dots
                    </button>
                    <button
                      type="button"
                      onClick={() => setFsNormalize((prev) => !prev)}
                      className={`ticker-fs-control-btn ${fsNormalize ? 'is-active' : ''}`}
                      title="Scale series to 0-100 for visual comparison"
                    >
                      Normalize
                    </button>
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() => setFullscreenChart(null)}
                  className="ticker-fullscreen-modal-close p-2 rounded-lg text-white transition-colors shrink-0 hover:opacity-90"
                  style={{ background: 'rgba(212, 175, 55, 0.15)', border: '1px solid rgba(212, 175, 55, 0.25)' }}
                  aria-label="Close"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
              <div className="flex-1 min-h-0 p-4">
            {fullscreenChart === 'mentions-trend' && (
              <motion.div
                key="fs-mentions-trend"
                className="ticker-fs-chart-shell ticker-fs-chart-shell--gold"
                initial={{ opacity: 0, y: 12, scale: 0.985 }}
                animate={{ opacity: 1, y: 0, scale: 1 }}
                transition={{ duration: 0.3 }}
              >
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={fullscreenMentionsData} margin={{ ...CHART_MARGIN, top: 8, right: 16 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.06)" />
                    <XAxis dataKey="hour" stroke="#bbb" tick={{ fontSize: 11, fill: '#bbb' }} interval={xAxisInterval} tickFormatter={chartXAxisTickFormatter} angle={-40} textAnchor="end" height={56} />
                    <YAxis stroke="#bbb" tick={{ fontSize: 11, fill: '#bbb' }} width={44} allowDecimals={false} tickFormatter={(v) => fsNormalize ? `${v}%` : `${v}`} />
                    <Tooltip content={({ active, payload, label }) => {
                      if (active && payload && payload.length) {
                        const dataIndex = payload[0]?.payload ? stats.hourly_data.findIndex((d: any) => d.hour === label) : -1;
                        const dataPoint = dataIndex >= 0 ? stats.hourly_data[dataIndex] : null;
                        const timeLabel = formatLocalTime(dataPoint?.timestampUtc, typeof label === 'string' || typeof label === 'number' ? label : undefined);
                        return (<div className="ticker-fs-tooltip"><p className="text-white font-medium mb-2">{timeLabel}</p><p className="text-brand-gold text-sm font-semibold">Mentions: {Number(payload[0]?.value ?? 0).toLocaleString()}</p></div>);
                      }
                      return null;
                    }} />
                    <Legend content={(props: any) => <AnimatedLegend payload={props.payload} />} />
                    <Line type={fsLineType} dataKey="mentions" name="Mentions" stroke="#D4AF37" strokeWidth={2.5} dot={fsShowDots ? { ...fsDot, fill: '#D4AF37', strokeWidth: 2 } : false} activeDot={{ r: 6, stroke: '#D4AF37', strokeWidth: 2 }} />
                  </LineChart>
                </ResponsiveContainer>
              </motion.div>
            )}
            {fullscreenChart === 'mentions-over-time' && (
              <motion.div
                key="fs-mentions-over-time"
                className="ticker-fs-chart-shell ticker-fs-chart-shell--gold"
                initial={{ opacity: 0, y: 12, scale: 0.985 }}
                animate={{ opacity: 1, y: 0, scale: 1 }}
                transition={{ duration: 0.3 }}
              >
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={fullscreenMentionsData} margin={{ ...CHART_MARGIN, top: 8, right: 16 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.06)" />
                    <XAxis dataKey="hour" stroke="#bbb" tick={{ fontSize: 11, fill: '#bbb' }} interval={xAxisInterval} tickFormatter={chartXAxisTickFormatter} angle={-40} textAnchor="end" height={56} />
                    <YAxis stroke="#bbb" tick={{ fontSize: 11, fill: '#bbb' }} width={44} allowDecimals={false} tickFormatter={(v) => fsNormalize ? `${v}%` : `${v}`} />
                    <Tooltip content={({ active, payload, label }) => {
                      if (active && payload && payload.length) {
                        const dataIndex = payload[0]?.payload ? stats.hourly_data.findIndex((d: any) => d.hour === label) : -1;
                        const dataPoint = dataIndex >= 0 ? stats.hourly_data[dataIndex] : null;
                        const timeLabel = formatLocalTime(dataPoint?.timestampUtc, typeof label === 'string' || typeof label === 'number' ? label : undefined);
                        return (<div className="ticker-fs-tooltip"><p className="text-white font-medium mb-2">{timeLabel}</p><p className="text-brand-gold text-sm font-semibold">Mentions: {Number(payload[0]?.value ?? 0).toLocaleString()}</p></div>);
                      }
                      return null;
                    }} />
                    <Legend content={(props: any) => <AnimatedLegend payload={props.payload} />} />
                    <Line type={fsLineType} dataKey="mentions" name="Mentions" stroke="#D4AF37" strokeWidth={2.5} dot={fsShowDots ? { ...fsDot, fill: '#D4AF37', strokeWidth: 2 } : false} activeDot={{ r: 6, stroke: '#D4AF37', strokeWidth: 2 }} />
                  </LineChart>
                </ResponsiveContainer>
              </motion.div>
            )}
            {fullscreenChart === 'discussion' && (
              <motion.div
                key="fs-discussion"
                className="ticker-fs-chart-shell ticker-fs-chart-shell--teal"
                initial={{ opacity: 0, y: 12, scale: 0.985 }}
                animate={{ opacity: 1, y: 0, scale: 1 }}
                transition={{ duration: 0.3 }}
              >
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={fullscreenDiscussionData} margin={{ ...CHART_MARGIN, top: 8, right: 16 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.06)" />
                    <XAxis dataKey="hour" stroke="#bbb" tick={{ fontSize: 11, fill: '#bbb' }} interval={xAxisInterval} tickFormatter={chartXAxisTickFormatter} angle={-40} textAnchor="end" height={56} />
                    <YAxis stroke="#bbb" tick={{ fontSize: 11, fill: '#bbb' }} width={44} allowDecimals={false} tickFormatter={(v) => fsNormalize ? `${v}%` : `${v}`} />
                    <Tooltip content={({ active, payload, label }) => {
                      if (active && payload && payload.length) {
                        const dataIndex = payload[0]?.payload ? stats.hourly_data.findIndex((d: any) => d.hour === label) : -1;
                        const dataPoint = dataIndex >= 0 ? stats.hourly_data[dataIndex] : null;
                        const timeLabel = formatLocalTime(dataPoint?.timestampUtc, typeof label === 'string' || typeof label === 'number' ? label : undefined);
                        return (<div className="ticker-fs-tooltip"><p className="text-white font-medium mb-2">{timeLabel}</p><p className="text-brand-gold text-sm">Volume: {Number(payload.find(p => p.dataKey === 'discussionVolume')?.value ?? 0).toLocaleString()}</p><p className="text-emerald-400 text-sm">Participants: {Number(payload.find(p => p.dataKey === 'discussionParticipants')?.value ?? 0).toLocaleString()}</p></div>);
                      }
                      return null;
                    }} />
                    <Legend content={(props: any) => <AnimatedLegend payload={props.payload} />} />
                    <Line type={fsLineType} dataKey="discussionVolume" name="Volume" stroke="#D4AF37" strokeWidth={2.5} dot={fsShowDots ? { ...fsDot, fill: '#D4AF37' } : false} connectNulls activeDot={{ r: 6 }} />
                    <Line type={fsLineType} dataKey="discussionParticipants" name="Participants" stroke="#10b981" strokeWidth={2.5} dot={fsShowDots ? { ...fsDot, fill: '#10b981' } : false} connectNulls activeDot={{ r: 6 }} />
                  </LineChart>
                </ResponsiveContainer>
              </motion.div>
            )}
            {fullscreenChart === 'sentiment' && stats.hourly_data && stats.hourly_data.length > 0 && (
              <motion.div
                key="fs-sentiment"
                className="ticker-fs-chart-shell ticker-fs-chart-shell--sentiment"
                initial={{ opacity: 0, y: 12, scale: 0.985 }}
                animate={{ opacity: 1, y: 0, scale: 1 }}
                transition={{ duration: 0.3 }}
              >
                <ResponsiveContainer width="100%" height="100%">
                  <AreaChart data={stats.hourly_data.map((d: any) => ({ ...d, positiveSentiment: d.sentiment != null && d.sentiment > 0 ? d.sentiment : null, negativeSentiment: d.sentiment != null && d.sentiment < 0 ? d.sentiment : null }))} margin={{ ...CHART_MARGIN, top: 8, right: 16 }}>
                    <defs>
                      <linearGradient id="colorPositiveFs" x1="0" y1="0" x2="0" y2="1"><stop offset="5%" stopColor="#10b981" stopOpacity={0.8}/><stop offset="95%" stopColor="#10b981" stopOpacity={0.05}/></linearGradient>
                      <linearGradient id="colorNegativeFs" x1="0" y1="1" x2="0" y2="0"><stop offset="5%" stopColor="#ef4444" stopOpacity={0.8}/><stop offset="95%" stopColor="#ef4444" stopOpacity={0.05}/></linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.06)" />
                    <XAxis dataKey="hour" stroke="#bbb" tick={{ fontSize: 11, fill: '#bbb' }} interval={xAxisInterval} tickFormatter={chartXAxisTickFormatter} angle={-40} textAnchor="end" height={56} />
                    <YAxis stroke="#bbb" tick={{ fontSize: 11, fill: '#bbb' }} width={44} domain={[-1, 1]} tickFormatter={(v) => `${(v * 100).toFixed(0)}%`} />
                    <Tooltip content={({ active, payload, label }) => {
                      if (active && payload && payload.length) {
                        const sentiment = payload.find((p: any) => p.dataKey === 'sentiment')?.value as number;
                        const dataIndex = payload[0]?.payload ? stats.hourly_data.findIndex((d: any) => d.hour === label) : -1;
                        const dataPoint = dataIndex >= 0 ? stats.hourly_data[dataIndex] : null;
                        const timeLabel = formatLocalTime(dataPoint?.timestampUtc, typeof label === 'string' || typeof label === 'number' ? label : undefined);
                        return (<div className="ticker-fs-tooltip"><p className="text-white font-medium mb-2">{timeLabel}</p><p className={`text-sm font-semibold ${sentiment != null ? (sentiment > 0.1 ? 'text-emerald-500' : sentiment < -0.1 ? 'text-red-500' : 'text-amber-500') : 'text-[#bbb]'}`}>Sentiment: {sentiment != null ? `${(sentiment * 100).toFixed(1)}%` : 'N/A'}</p></div>);
                      }
                      return null;
                    }} />
                    <ReferenceLine y={0} stroke="#666" strokeDasharray="2 2" strokeWidth={1.5} />
                    <Legend content={(props: any) => <AnimatedLegend payload={props.payload} />} />
                    <Area type="monotone" dataKey="positiveSentiment" name="Positive" stroke="#10b981" strokeWidth={2} fill="url(#colorPositiveFs)" connectNulls={false} />
                    <Area type="monotone" dataKey="negativeSentiment" name="Negative" stroke="#ef4444" strokeWidth={2} fill="url(#colorNegativeFs)" connectNulls={false} />
                    <Line type={fsLineType} dataKey="sentiment" name="Net sentiment" stroke="#D4AF37" strokeWidth={2} dot={fsShowDots ? ((props: any) => { const { cx, cy, payload, index } = props; const key = payload?.timestampUtc ?? payload?.hour ?? index ?? 0; if (payload?.sentiment == null) return <circle key={key} cx={cx} cy={cy} r={0} fill="transparent" />; const color = payload.sentiment > 0.1 ? '#10b981' : payload.sentiment < -0.1 ? '#ef4444' : '#f59e0b'; return <circle key={key} cx={cx} cy={cy} r={4} fill={color} stroke="#fff" strokeWidth={2} />; }) : false} activeDot={{ r: 6 }} connectNulls={false} />
                  </AreaChart>
                </ResponsiveContainer>
              </motion.div>
            )}
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>

      <UpgradeModal
        isOpen={showUpgradeModal}
        onClose={() => setShowUpgradeModal(false)}
        onSubscribe={() => {
          setShowUpgradeModal(false);
          navigate('/login?redirect=' + encodeURIComponent('/billing?checkout=1'));
        }}
      />
    </div>
  );
};

export default TickerDetails; 
